/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef, Suspense, useMemo, useState } from "react";
import { css, keyframes } from "@emotion/react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { Area, AreaChart, Bar, BarChart, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { Flex, Heading, Icon, Icons, Link, Loading, Text, useTimeRange } from "@phoenix/components";
import { useNotifySuccess } from "@phoenix/contexts";
import { ConnectedTimeRangeSelector, TimeRangeProvider } from "@phoenix/components/datetime";
import { GradientCircle } from "@phoenix/components/project/GradientCircle";
import { tableCSS } from "@phoenix/components/table/styles";
import { ONE_MONTH_MS } from "@phoenix/constants/timeConstants";
import { costFormatter, intShortFormatter } from "@phoenix/utils/numberFormatUtils";
import { FolderOutline, ListOutline, DollarSignOutline, CubeOutline, ClockOutline } from "@phoenix/components/core/icon/Icons";

import type { GlobalDashboardPageQuery } from "./__generated__/GlobalDashboardPageQuery.graphql";
import type { GlobalDashboardPageDrawerQuery } from "./__generated__/GlobalDashboardPageDrawerQuery.graphql";
import { ErrorBoundary } from "@phoenix/components/exception";

import { HealthOverviewCard } from "./components/HealthOverviewCard";
import { TopProjectsNeedingAttentionCard } from "./components/TopProjectsNeedingAttentionCard";
import { AvailableEvaluationsCard } from "./components/AvailableEvaluationsCard";
import { ActiveProjectsHealthCard } from "./components/ActiveProjectsHealthCard";
import { ProjectPerformanceChartCard } from "./components/ProjectPerformanceChartCard";

// Enhanced animations
const pulseGlow = keyframes`
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.02); }
`;

const floatUp = keyframes`
  0% { transform: translateY(20px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pageCSS = css`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 24px;
  box-sizing: border-box;
  background: var(--global-background-color-default);
  position: relative;
`;

const FunnelIcon = ({ active }: { active?: boolean }) => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill={active ? "#00685f" : "none"} stroke={active ? "#00685f" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline-block" }}>
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const headerCSS = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
`;

const titleCSS = css`
  font-size: 28px;
  font-weight: 900;
  color: #000000;
  letter-spacing: -0.5px;
  animation: ${floatUp} 0.6s ease-out;
`;

const subtitleCSS = css`
  font-size: 13px;
  color: #1e293b;
  margin-top: 2px;
  font-weight: 700;
`;

const splitGridCSS = css`
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
  align-items: stretch;
  gap: 10px;
  margin-bottom: 10px;
  position: relative;
  z-index: 1;
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const evalGridCSS = css`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  align-items: stretch;
  gap: 10px;
  margin-bottom: 10px;
  position: relative;
  z-index: 1;
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const columnCSS = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: ${floatUp} 0.6s ease-out;
`;

const globalStatGridCSS = css`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  margin-bottom: 10px;
  position: relative;
  z-index: 1;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const globalStatCardCSS = css`
  position: relative;
  background: #ffffff;
  border: 1px solid var(--global-border-color-default, #e1e3e4);
  border-radius: 14px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }
`;

const globalIconContainerCSS = (bg: string, fg: string) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: ${bg};
  color: ${fg};
  margin-bottom: 12px;
  
  & svg {
    width: 20px;
    height: 20px;
    stroke: currentColor;
    stroke-width: 2.5;
    fill: none;
  }
`;

const globalStatLabelCSS = css`
  font-size: 14px;
  font-weight: 900;
  color: #000000;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const globalStatValueCSS = css`
  font-size: 32px;
  font-weight: 900;
  color: #000000;
  line-height: 1.1;
  margin-bottom: 6px;
  letter-spacing: -0.5px;
`;

const globalStatHintCSS = css`
  font-size: 13px;
  font-weight: 800;
  color: #000000;
`;

const statGridCSS = css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const statCardCSS = (accent: string, gradient: string) => css`
  position: relative;
  background: var(--global-color-gray-75);
  border: 1px solid var(--global-border-color-default);
  border-top: 3px solid ${accent};
  border-radius: 16px;
  padding: 16px 20px 0px 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 140px;
  overflow: hidden;
  transition: all 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${gradient};
    opacity: 0;
    transition: opacity 0.5s ease;
    border-radius: 16px;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px -8px ${accent}30;
    border-color: ${accent};

    &::before {
      opacity: 0.04;
    }
  }
`;

const statLabelCSS = css`
  font-size: 11px;
  font-weight: 700;
  color: var(--global-text-color-500);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
`;

const statValueCSS = css`
  font-size: 28px;
  font-weight: 800;
  color: var(--global-text-color-900);
  line-height: 1.1;
  letter-spacing: -0.5px;
  position: relative;
  z-index: 1;
`;

const chartCardCSS = css`
  background: #ffffff;
  border: 1px solid var(--global-border-color-default, #e1e3e4);
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  height: 100%;
  box-sizing: border-box;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--global-color-gray-300);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }
`;

const tableCardCSS = css`
  background: var(--global-color-gray-75);
  border: 1px solid var(--global-border-color-default);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--global-color-gray-300);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const tableTitleCSS = css`
  font-size: 16px;
  font-weight: 700;
  color: var(--global-text-color-900);
  margin-bottom: 16px;
  letter-spacing: -0.3px;
`;

const chartColors = [
  "#3b82f6", "#10b981", "#8b5cf6", "#f59e0b", "#06b6d4", "#ec4899",
  "#f97316", "#6366f1", "#14b8a6", "#d946ef"
];

// Custom animated pie label
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }: any) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#374151"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize="11"
      fontWeight="600"
      opacity={0.9}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export function GlobalDashboardPage() {
  return (
    <TimeRangeProvider>
      <GlobalDashboardPageContent />
    </TimeRangeProvider>
  );
}

// ─── Annotation metadata config ──────────────────────────────────────────────
const ANN_META: Record<string, { label: string; kind: 'LLM' | 'CODE'; icon: string; color: string }> = {
  ann_instruction: { label: 'Instruction Compliance', kind: 'LLM', icon: '📜', color: '#3b82f6' },
  ann_prompt: { label: 'Prompt Adherence', kind: 'LLM', icon: '📌', color: '#8b5cf6' },
  ann_role: { label: 'Role Consistency', kind: 'LLM', icon: '🎭', color: '#06b6d4' },
  ann_system: { label: 'System Prompt Leakage', kind: 'LLM', icon: '🔐', color: '#ec4899' },
  ann_fewshot: { label: 'Few-Shot Effectiveness', kind: 'LLM', icon: '✨', color: '#f59e0b' },
  traceAnnotationSummary: { label: 'General Correctness', kind: 'LLM', icon: '🎯', color: '#10b981' },
  ann_translation: { label: 'Translation Accuracy', kind: 'LLM', icon: '🌐', color: '#3b82f6' },
  ann_crosslang: { label: 'Cross-Language Consistency', kind: 'LLM', icon: '🔀', color: '#8b5cf6' },
  ann_cultural: { label: 'Cultural Appropriateness', kind: 'LLM', icon: '🗺️', color: '#06b6d4' },
  ann_format: { label: 'Format Compliance', kind: 'LLM', icon: '📄', color: '#ec4899' },
  ann_schema: { label: 'Schema Validation', kind: 'LLM', icon: '📋', color: '#f59e0b' },
  ann_reqfields: { label: 'Required Fields Present', kind: 'LLM', icon: '📝', color: '#10b981' },
  ann_structure: { label: 'Structure Correctness', kind: 'LLM', icon: '🏗️', color: '#3b82f6' },
  ann_regex: { label: 'Regex Match', kind: 'LLM', icon: '🔍', color: '#8b5cf6' },
  ann_syntax: { label: 'Syntax Correctness', kind: 'LLM', icon: '💻', color: '#06b6d4' },
  ann_compile: { label: 'Compilation Success', kind: 'LLM', icon: '⚙️', color: '#ec4899' },
  ann_efficiency: { label: 'Efficiency of Code', kind: 'LLM', icon: '⏳', color: '#f59e0b' },
  ann_testcase: { label: 'Test Case Pass Rate', kind: 'LLM', icon: '✅', color: '#10b981' },
  ann_security: { label: 'Security Vulnerabilities', kind: 'LLM', icon: '🛡️', color: '#ef4444' },
  ann_helpfulness: { label: 'Helpfulness', kind: 'LLM', icon: '🤝', color: '#3b82f6' },
  ann_satisfaction: { label: 'Satisfaction Score', kind: 'LLM', icon: '⭐', color: '#8b5cf6' },
  ann_responsetime: { label: 'Response Time Perception', kind: 'LLM', icon: '⏱️', color: '#06b6d4' },
  ann_convquality: { label: 'Conversational Quality', kind: 'LLM', icon: '💬', color: '#ec4899' },
  ann_domain: { label: 'Domain Accuracy', kind: 'LLM', icon: '📚', color: '#f59e0b' },
  ann_uptodate: { label: 'Up-to-Date Knowledge', kind: 'LLM', icon: '📅', color: '#10b981' },
  ann_factcheck: { label: 'Fact-Checking Accuracy', kind: 'LLM', icon: '🧐', color: '#3b82f6' },
  ann_citation: { label: 'Citation Correctness', kind: 'LLM', icon: '🏷️', color: '#8b5cf6' },
  ann_moderation: { label: 'Moderation', kind: 'LLM', icon: '⚖️', color: '#ec4899' },
  ann_closed_qa: { label: 'Model-Graded Closed QA', kind: 'LLM', icon: '🚪', color: '#10b981' },
  ann_token: { label: 'Token Breakdown', kind: 'CODE', icon: '🪙', color: '#007AFF' },
  ann_trace: { label: 'Trace Completeness', kind: 'CODE', icon: '🔗', color: '#6366f1' },
  ann_failure: { label: 'Failure Rates', kind: 'CODE', icon: '⚠️', color: '#ef4444' },
  ann_latency: { label: 'Span Latency', kind: 'CODE', icon: '⚡', color: '#f97316' },
  ann_retrieval: { label: 'Retrieval vs Generation', kind: 'CODE', icon: '🗃️', color: '#10b981' },
  ann_consistency: { label: 'Consistency Evaluation', kind: 'CODE', icon: '🔁', color: '#3b82f6' },
  ann_stability: { label: 'Stability Across Runs', kind: 'CODE', icon: '⚖️', color: '#8b5cf6' },
  ann_tempsens: { label: 'Temperature Sensitivity', kind: 'CODE', icon: '🌡️', color: '#06b6d4' },
  ann_determinism: { label: 'Determinism', kind: 'CODE', icon: '🎯', color: '#ec4899' },
  ann_sql: { label: 'Sql Evaluation', kind: 'CODE', icon: '🗄️', color: '#f59e0b' },
  ann_toolusage: { label: 'Tool Usage Correctness', kind: 'CODE', icon: '🔧', color: '#3b82f6' },
  ann_toolinvoke: { label: 'Tool Invocation', kind: 'CODE', icon: '🛠️', color: '#8b5cf6' },
  ann_reasoning: { label: 'Multi-Step Reasoning', kind: 'CODE', icon: '🧠', color: '#06b6d4' },
  ann_contains: { label: 'Contains Substring', kind: 'CODE', icon: '🔤', color: '#10b981' },
  ann_refusal: { label: 'Is-Refusal', kind: 'CODE', icon: '🙅', color: '#ef4444' },
  ann_perplexity: { label: 'Perplexity', kind: 'CODE', icon: '📉', color: '#f97316' },
};

const normalizeAnnotationName = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]/g, "");

// Chat-bubble style evaluation feed
function EvaluationChatFeed({
  projects,
  avgCorrectness,
  totalEvaluatedTraces,
}: {
  projects: any[];
  avgCorrectness: number;
  totalEvaluatedTraces: number;
}) {
  // Consolidate annotation data across all projects
  const annotationMap = useMemo(() => {
    const map: Record<string, { totalScore: number; count: number; projectCount: number }> = {};
    for (const p of projects) {
      for (const key of Object.keys(ANN_META)) {
        const summary = (p as any)[key];
        if (summary && (summary.count ?? 0) > 0) {
          if (!map[key]) map[key] = { totalScore: 0, count: 0, projectCount: 0 };
          map[key].totalScore += (summary.meanScore ?? 0) * summary.count;
          map[key].count += summary.count;
          map[key].projectCount += 1;
        }
      }
    }
    return map;
  }, [projects]);

  const annotations = useMemo(() => {
    return Object.entries(ANN_META)
      .map(([key, meta]) => {
        const data = annotationMap[key];
        const avgScore = data && data.count > 0 ? data.totalScore / data.count : null;
        return { key, ...meta, avgScore, totalCount: data?.count ?? 0, projectCount: data?.projectCount ?? 0 };
      })
      .sort((a, b) => {
        if ((a.avgScore !== null) !== (b.avgScore !== null)) return a.avgScore !== null ? -1 : 1;
        return (b.avgScore ?? 0) - (a.avgScore ?? 0);
      });
  }, [annotationMap]);

  const [filter, setFilter] = React.useState<'ALL' | 'LLM' | 'CODE'>('ALL');
  const [hovered, setHovered] = React.useState<string | null>(null);
  const [scoreFilter, setScoreFilter] = React.useState<'ALL' | 'PASS' | 'BORDERLINE' | 'FAIL'>('ALL');
  const [expandedKey, setExpandedKey] = React.useState<string | null>(null);
  const [hoveredSegment, setHoveredSegment] = React.useState<string | null>(null);
  const [needsAttentionOpen, setNeedsAttentionOpen] = React.useState(true);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedMetric, setSelectedMetric] = React.useState<any>(null);
  const { timeRange } = useTimeRange();

  // activeAnnotations are those of the active category filter (ALL/LLM/CODE) that have data
  const activeAnnotations = useMemo(() => {
    return annotations.filter(a => (filter === 'ALL' || a.kind === filter) && a.avgScore !== null);
  }, [annotations, filter]);

  const visibleAnnotations = useMemo(() => {
    return activeAnnotations.filter(a => {
      if (scoreFilter === 'ALL') return true;
      if (scoreFilter === 'PASS') return (a.avgScore ?? 0) >= 80;
      if (scoreFilter === 'BORDERLINE') return (a.avgScore ?? 0) >= 60 && (a.avgScore ?? 0) < 80;
      if (scoreFilter === 'FAIL') return (a.avgScore ?? 0) < 60;
      return true;
    });
  }, [activeAnnotations, scoreFilter]);

  const hasAnyData = activeAnnotations.length > 0;

  // Donut chart data
  const passCount = activeAnnotations.filter(a => (a.avgScore ?? 0) >= 80).length;
  const borderlineCount = activeAnnotations.filter(a => (a.avgScore ?? 0) >= 60 && (a.avgScore ?? 0) < 80).length;
  const failCount = activeAnnotations.filter(a => (a.avgScore ?? 0) < 60).length;
  const donutSegments = [
    { label: 'Pass', value: passCount, color: '#007AFF' },
    { label: 'Borderline', value: borderlineCount, color: '#C7820E' },
    { label: 'Fail', value: failCount, color: '#ef4444' },
  ].filter(s => {
    if (s.value === 0) return false;
    if (scoreFilter === 'ALL') return true;
    if (scoreFilter === 'PASS') return s.label === 'Pass';
    if (scoreFilter === 'BORDERLINE') return s.label === 'Borderline';
    if (scoreFilter === 'FAIL') return s.label === 'Fail';
    return true;
  });
  const donutTotal = donutSegments.reduce((s, d) => s + d.value, 0);

  // SVG donut chart — segment arcs from 12 o'clock
  const CX = 150, CY = 140, R = 58, SW = 18;
  const C = 2 * Math.PI * R;

  const donutArcs = useMemo(() => {
    let cumPct = 0;
    return donutSegments.map(seg => {
      const pct = donutTotal > 0 ? seg.value / donutTotal : 0;
      const dashLen = pct * C;
      const dashOff = -cumPct * C;
      cumPct += pct;
      return { ...seg, dashLen, dashOff };
    });
  }, [donutSegments, donutTotal, C]);

  // Angle computations using unfiltered segments so angles stay stable
  const callouts = useMemo(() => {
    const allDonutSegments = [
      { label: 'Pass', value: passCount, color: '#007AFF' },
      { label: 'Borderline', value: borderlineCount, color: '#C7820E' },
      { label: 'Fail', value: failCount, color: '#ef4444' },
    ].filter(s => s.value > 0);
    const unfilteredTotal = allDonutSegments.reduce((s, d) => s + d.value, 0);

    let cumPctCallout = 0;
    return allDonutSegments.map(seg => {
      const pct = unfilteredTotal > 0 ? seg.value / unfilteredTotal : 0;
      const midPct = cumPctCallout + pct / 2;
      cumPctCallout += pct;
      const angle = midPct * 2 * Math.PI - Math.PI / 2;
      const xStart = CX + R * Math.cos(angle);
      const yStart = CY + R * Math.sin(angle);
      const xMid = CX + (R + 26) * Math.cos(angle);
      const yMid = CY + (R + 26) * Math.sin(angle);
      const isLeft = xMid < CX;
      const xEnd = isLeft ? xMid - 30 : xMid + 30;
      const yEnd = yMid;
      const percentStr = `${(pct * 100).toFixed(0)}%`;
      return {
        label: seg.label,
        color: seg.color,
        points: `${xStart},${yStart} ${xMid},${yMid} ${xEnd},${yEnd}`,
        midX: xMid,
        midY: yMid,
        textX: isLeft ? xEnd - 4 : xEnd + 4,
        textY: yEnd + 3,
        textAnchor: (isLeft ? 'end' : 'start') as "start" | "end" | "inherit" | "middle" | undefined,
        percentStr
      };
    });
  }, [passCount, borderlineCount, failCount, CX, CY, R]);

  const avgOverall = useMemo(() => {
    return visibleAnnotations.length > 0
      ? visibleAnnotations.reduce((s, a) => s + Math.min(a.avgScore ?? 0, 100), 0) / visibleAnnotations.length
      : 0;
  }, [visibleAnnotations]);

  const needsAttention = useMemo(() => {
    return activeAnnotations
      .filter(a => (a.avgScore ?? 0) < 80)
      .sort((a, b) => (a.avgScore ?? 0) - (b.avgScore ?? 0))
      .slice(0, 3);
  }, [activeAnnotations]);

  return (
    <div css={css`
      background: var(--global-color-gray-75);
      border: 1px solid var(--global-border-color-default);
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 2px 12px rgba(0,0,0,0.07);
    `}>

      {/* ── Header with filter tabs ── */}
      <div css={css`
        padding: 12px 16px 0;
        background: linear-gradient(135deg, var(--global-color-gray-100) 0%, var(--global-color-gray-200) 100%);
        border-bottom: 1px solid var(--global-border-color-default);
      `}>
        <div css={css`display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;`}>
          <div css={css`font-size: 13px; font-weight: 700; color: var(--global-text-color-900); display: flex; align-items: center; gap: 8px;`}>
            Annotation Results
            <span css={css`
              font-size: 10px; font-weight: 600; color: #8b5cf6;
              background: var(--global-color-gray-100); border: 1px solid var(--global-border-color-default);
              padding: 2px 8px; border-radius: 20px;
            `}>{activeAnnotations.length} active</span>
          </div>
        </div>

        {/* Filter Tabs */}
        <div css={css`display: flex; gap: 4px;`}>
          {(['ALL', 'LLM', 'CODE'] as const).map(tab => {
            const isActive = filter === tab;
            const tc = {
              ALL: { fg: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb' },
              LLM: { fg: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
              CODE: { fg: '#10b981', bg: '#ecfdf5', border: '#a7f3d0' },
            }[tab];
            return (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                css={css`
                  font-size: 10px; font-weight: 700;
                  padding: 5px 13px;
                  border-radius: 8px 8px 0 0;
                  border: 1px solid ${isActive ? tc.border : 'transparent'};
                  border-bottom: none;
                  background: ${isActive ? 'var(--global-color-gray-75)' : 'transparent'};
                  color: ${isActive ? tc.fg : 'var(--global-text-color-500)'};
                  cursor: pointer;
                  transition: all 0.18s ease;
                  position: relative; bottom: -1px;
                  &:hover { color: ${tc.fg}; background: var(--global-color-gray-100); }
                `}
              >
                {tab === 'LLM' ? 'LLM' : tab === 'CODE' ? 'CODE' : '✦ ALL'}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Charts Row: Center Donut Chart & Status Filters ── */}
      {hasAnyData && (
        <div css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 24px 20px 14px;
          border-bottom: 1px solid var(--global-border-color-default);
          background: var(--global-color-gray-50);
          position: relative;
        `}>
          {/* HUD Corner Brackets framing the chart area */}
          <div css={css`
            position: absolute; top: 12px; left: 12px; width: 10px; height: 10px;
            border-top: 1.5px solid rgba(255, 255, 255, 0.12); border-left: 1.5px solid rgba(255, 255, 255, 0.12);
            pointer-events: none;
          `} />
          <div css={css`
            position: absolute; top: 12px; right: 12px; width: 10px; height: 10px;
            border-top: 1.5px solid rgba(255, 255, 255, 0.12); border-right: 1.5px solid rgba(255, 255, 255, 0.12);
            pointer-events: none;
          `} />
          <div css={css`
            position: absolute; bottom: 12px; left: 12px; width: 10px; height: 10px;
            border-bottom: 1.5px solid rgba(255, 255, 255, 0.12); border-left: 1.5px solid rgba(255, 255, 255, 0.12);
            pointer-events: none;
          `} />
          <div css={css`
            position: absolute; bottom: 12px; right: 12px; width: 10px; height: 10px;
            border-bottom: 1.5px solid rgba(255, 255, 255, 0.12); border-right: 1.5px solid rgba(255, 255, 255, 0.12);
            pointer-events: none;
          `} />

          {/* Row container for Donut + Side Legend */}
          <div css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
            gap: 32px;
            width: 100%;
            margin-top: 8px;
            margin-bottom: 8px;
          `}>
            {/* Centered Large Donut chart with Rotating HUD Rings */}
            <svg width="300" height="280" viewBox="0 0 300 280" css={css`
              @keyframes lockon-spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              @keyframes lockon-spin-reverse {
                0% { transform: rotate(360deg); }
                100% { transform: rotate(0deg); }
              }
            `}>
              {/* Outer Rotating Dotted Ring */}
              <circle
                cx={CX} cy={CY} r={R + 10}
                fill="none"
                stroke="rgba(255, 255, 255, 0.1)"
                strokeWidth="1"
                strokeDasharray="4 4"
                css={css`
                  transform-origin: 150px 140px;
                  animation: lockon-spin 25s linear infinite;
                `}
              />

              {/* Inner Rotating Dotted Ring */}
              <circle
                cx={CX} cy={CY} r={R - 9}
                fill="none"
                stroke="rgba(255, 255, 255, 0.08)"
                strokeWidth="1"
                strokeDasharray="2 3"
                css={css`
                  transform-origin: 150px 140px;
                  animation: lockon-spin-reverse 15s linear infinite;
                `}
              />

              {/* Background Track circle */}
              <circle cx={CX} cy={CY} r={R} fill="none" stroke="var(--global-border-color-default)" strokeWidth={SW} />

              {/* Colored Segments with Hover Neon Glow */}
              {donutArcs.map((arc, i) => {
                const isSegmentHovered = hoveredSegment === arc.label;
                return (
                  <circle
                    key={i}
                    cx={CX} cy={CY} r={R}
                    fill="none"
                    stroke={arc.color}
                    strokeWidth={isSegmentHovered ? SW + 3 : SW}
                    strokeDasharray={`${arc.dashLen} ${C}`}
                    strokeDashoffset={arc.dashOff}
                    strokeLinecap="butt"
                    transform={`rotate(-90 ${CX} ${CY})`}
                    onMouseEnter={() => setHoveredSegment(arc.label)}
                    onMouseLeave={() => setHoveredSegment(null)}
                    style={{ transition: 'stroke-dasharray 0.8s ease, stroke-width 0.3s ease' }}
                    css={css`
                      cursor: pointer;
                      filter: ${isSegmentHovered ? `drop-shadow(0 0 6px ${arc.color})` : 'none'};
                      transition: filter 0.3s ease;
                    `}
                  />
                );
              })}

              {/* Centered Avg text */}
              <text x={CX} y={CY - 4} textAnchor="middle" fill="var(--global-text-color-900)" fontSize="20" fontWeight="800">{avgOverall.toFixed(0)}%</text>
              <text x={CX} y={CY + 12} textAnchor="middle" fill="var(--global-text-color-500)" fontSize="10">avg score</text>

              {/* Jarvis HUD Callout Lines & Text badges */}
              {callouts.map(c => {
                const isSegmentActive = donutSegments.some(s => s.label === c.label);
                const isHovered = hoveredSegment === c.label && isSegmentActive;
                if (!isSegmentActive) return null;
                return (
                  <g
                    key={c.label}
                    css={css`
                      opacity: ${isHovered ? 1 : 0};
                      pointer-events: none;
                      transition: opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1), transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                      transform: ${isHovered ? 'translate(0, 0)' : 'scale(0.95)'};
                      transform-origin: 150px 140px;
                    `}
                  >
                    {/* Glowing line */}
                    <polyline
                      points={c.points}
                      fill="none"
                      stroke={c.color}
                      strokeWidth="1.2"
                      strokeDasharray="2 2"
                      css={css`
                        filter: drop-shadow(0 0 2px ${c.color});
                      `}
                    />

                    {/* Small radar anchor dot on the bend */}
                    <circle
                      cx={c.midX}
                      cy={c.midY}
                      r="1.5"
                      fill={c.color}
                      css={css`
                        filter: drop-shadow(0 0 2px ${c.color});
                        animation: hud-dot-pulse 1s ease-in-out infinite;
                        @keyframes hud-dot-pulse {
                          0%, 100% { transform: scale(1); opacity: 1; }
                          50% { transform: scale(1.6); opacity: 0.5; }
                        }
                        transform-origin: ${c.midX}px ${c.midY}px;
                      `}
                    />

                    {/* Glowing percent text */}
                    <text
                      x={c.textX}
                      y={c.textY}
                      textAnchor={c.textAnchor}
                      fill={c.color}
                      fontSize="8.5"
                      fontWeight="800"
                      fontFamily="monospace, monospace"
                      css={css`
                        filter: drop-shadow(0 0 2px ${c.color}66);
                      `}
                    >
                      [{c.percentStr}]
                    </text>
                  </g>
                );
              })}
            </svg>

            {/* Vertical Legends */}
            <div css={css`
              display: flex;
              flex-direction: column;
              gap: 14px;
              min-width: 190px;
              padding-left: 16px;
              border-left: 1px solid var(--global-border-color-default);
            `}>
              {allDonutSegments.map(seg => {
                const isHov = hoveredSegment === seg.label;
                const total = unfilteredTotal || 1;
                const pct = (seg.value / total) * 100;
                const isSegmentActive = donutSegments.some(s => s.label === seg.label);

                return (
                  <div
                    key={seg.label}
                    onMouseEnter={() => isSegmentActive && setHoveredSegment(seg.label)}
                    onMouseLeave={() => isSegmentActive && setHoveredSegment(null)}
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                      cursor: ${isSegmentActive ? 'pointer' : 'default'};
                      transition: all 0.2s ease;
                      transform: ${isHov ? 'translateX(4px)' : 'none'};
                      opacity: ${isSegmentActive ? 1 : 0.25};
                    `}
                  >
                    <div css={css`display: flex; align-items: center; gap: 8px;`}>
                      <div css={css`
                        width: 7px; height: 7px; border-radius: 50%; background: ${seg.color}; flex-shrink: 0;
                        box-shadow: ${isHov ? `0 0 6px ${seg.color}` : 'none'};
                        transition: box-shadow 0.2s ease;
                      `} />
                      <span css={css`
                        font-size: 11px;
                        font-weight: 600;
                        color: ${isHov ? 'var(--global-text-color-900)' : 'var(--global-text-color-400)'};
                        transition: color 0.2s ease;
                      `}>
                        {seg.label}
                      </span>
                    </div>

                    <div css={css`display: flex; align-items: center; gap: 12px;`}>
                      <span css={css`
                        font-size: 11px;
                        font-weight: 700;
                        color: var(--global-text-color-900);
                      `}>
                        {seg.value}
                      </span>

                      <span css={css`
                        font-size: 9px;
                        font-weight: 800;
                        font-family: monospace, monospace;
                        color: ${seg.color};
                        background: ${seg.color}15;
                        border: 1px solid ${seg.color}25;
                        padding: 2px 8px;
                        border-radius: 12px;
                        min-width: 44px;
                        text-align: center;
                        box-shadow: ${isHov ? `0 0 6px ${seg.color}33` : 'none'};
                        transition: all 0.2s ease;
                      `}>
                        {pct.toFixed(0)}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Divider */}
          <div css={css`width: 90%; height: 1px; background: var(--global-border-color-default); margin: 16px 0 12px;`} />

          {/* Status Quick Filters */}
          <div css={css`display: flex; gap: 6px; align-items: center; justify-content: center; width: 100%;`}>
            <span css={css`font-size: 9px; font-weight: 700; color: var(--global-text-color-400); text-transform: uppercase; letter-spacing: 0.5px; margin-right: 4px;`}>
              Status:
            </span>
            {(['ALL', 'PASS', 'BORDERLINE', 'FAIL'] as const).map(status => {
              const isActive = scoreFilter === status;
              const count = {
                ALL: activeAnnotations.length,
                PASS: activeAnnotations.filter(a => (a.avgScore ?? 0) >= 80).length,
                BORDERLINE: activeAnnotations.filter(a => (a.avgScore ?? 0) >= 60 && (a.avgScore ?? 0) < 80).length,
                FAIL: activeAnnotations.filter(a => (a.avgScore ?? 0) < 60).length,
              }[status];
              const color = {
                ALL: 'var(--global-text-color-500)',
                PASS: '#10b981',
                BORDERLINE: '#f59e0b',
                FAIL: '#ef4444',
              }[status];
              return (
                <button
                  key={status}
                  onClick={() => setScoreFilter(status)}
                  css={css`
                    font-size: 9px; font-weight: 700;
                    padding: 3px 9px;
                    border-radius: 12px;
                    border: 1px solid ${isActive ? color : 'var(--global-border-color-default)'};
                    background: ${isActive ? color + '15' : 'transparent'};
                    color: ${isActive ? color : 'var(--global-text-color-500)'};
                    cursor: pointer;
                    transition: all 0.15s ease;
                    &:hover {
                      border-color: ${color};
                      color: ${color};
                    }
                  `}
                >
                  {status} ({count})
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Needs Attention Priority Widget ── */}
      {false && hasAnyData && (scoreFilter === 'ALL' || scoreFilter === 'FAIL') && needsAttention.length > 0 && (
        <div css={css`
          margin: 12px 14px 20px;
          padding: 14px 16px;
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(245, 158, 11, 0.05) 100%);
          border: 1px solid rgba(239, 68, 68, 0.15);
          border-left: 4px solid #ef4444;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.04);
        `}>
          <div
            onClick={() => setNeedsAttentionOpen(o => !o)}
            css={css`
              font-size: 11px; font-weight: 800; color: #ef4444;
              text-transform: uppercase; letter-spacing: 0.8px;
              display: flex; align-items: center; justify-content: space-between;
              cursor: pointer;
              user-select: none;
              margin-bottom: ${needsAttentionOpen ? '12px' : '0'};
            `}
          >
            <div css={css`display: flex; align-items: center; gap: 6px;`}>
              ⚠️ Needs Attention ({needsAttention.length})
            </div>
            <span css={css`
              font-size: 9px; color: #ef4444; transition: transform 0.25s ease;
              transform: rotate(${needsAttentionOpen ? '180deg' : '0deg'});
            `}>▼</span>
          </div>

          <div css={css`
            display: flex;
            flex-direction: column;
            gap: 10px;
            max-height: ${needsAttentionOpen ? '600px' : '0'};
            opacity: ${needsAttentionOpen ? 1 : 0};
            overflow: hidden;
            transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease;
          `}>
            {needsAttention.map(a => {
              const displayScore = Math.min(a.avgScore ?? 0, 100);
              return (
                <div
                  key={a.key}
                  onClick={() => { if (a.avgScore !== null) { setSelectedMetric(a); setDrawerOpen(true); } }}
                  css={css`
                    background: var(--global-color-gray-75);
                    border: 1px solid var(--global-border-color-default);
                    border-radius: 8px;
                    padding: 10px 14px;
                    cursor: pointer;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    position: relative;
                    overflow: hidden;
                    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                    
                    @keyframes sweep-line {
                      0% { transform: translateY(-100%); }
                      100% { transform: translateY(100%); }
                    }
                    
                    @keyframes lockon-spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                    
                    /* Jarvis Scanline overlay on hover */
                    &::before {
                      content: '';
                      position: absolute;
                      top: 0; left: 0; right: 0; bottom: 0;
                      background: linear-gradient(to bottom, rgba(239, 68, 68, 0) 0%, rgba(239, 68, 68, 0.04) 50%, rgba(239, 68, 68, 0) 100%);
                      height: 50%;
                      opacity: 0;
                      pointer-events: none;
                      transition: opacity 0.2s ease;
                    }
                    
                    &:hover {
                      border-color: rgba(239, 68, 68, 0.45);
                      box-shadow: 0 4px 14px rgba(239, 68, 68, 0.08);
                      transform: translateX(4px);
                      
                      &::before {
                        opacity: 1;
                        animation: sweep-line 1.5s linear infinite;
                      }
                      
                      /* Draw laser pointer line */
                      .hud-laser-line {
                        opacity: 0.8;
                        transform: scaleX(1);
                      }
                      
                      /* Spin lock-on ring */
                      .hud-lockon-ring {
                        opacity: 1;
                        animation: lockon-spin 3s linear infinite;
                      }
                    }
                  `}
                >
                  <div css={css`display: flex; align-items: center; justify-content: space-between; position: relative;`}>
                    {/* Icon & Label */}
                    <span css={css`
                      color: var(--global-text-color-900); 
                      font-size: 11px; 
                      font-weight: 700; 
                      display: flex; 
                      align-items: center; 
                      gap: 8px;
                      position: relative;
                      z-index: 2;
                    `}>
                      <span css={css`
                        position: relative; 
                        display: flex; 
                        align-items: center; 
                        justify-content: center;
                        width: 20px;
                        height: 20px;
                      `}>
                        {/* Lock-on target ring (visible on hover) */}
                        <span
                          className="hud-lockon-ring"
                          css={css`
                            position: absolute;
                            top: -3px; left: -3px; right: -3px; bottom: -3px;
                            border: 1px dashed #ef4444;
                            border-radius: 50%;
                            opacity: 0;
                            transition: opacity 0.3s ease;
                            pointer-events: none;
                          `}
                        />
                        <span css={css`position: relative; z-index: 2; font-size: 12px;`}>{a.icon}</span>
                      </span>
                      {a.label}
                    </span>

                    {/* Glowing Laser line */}
                    <div
                      className="hud-laser-line"
                      css={css`
                        position: absolute;
                        left: 140px;
                        right: 50px;
                        height: 1px;
                        background: repeating-linear-gradient(90deg, #ef4444 0px, #ef4444 2px, transparent 2px, transparent 4px);
                        opacity: 0;
                        transform: scaleX(0);
                        transform-origin: left;
                        transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease;
                        z-index: 1;
                        pointer-events: none;
                      `}
                    />

                    {/* Glowing HUD Badge score */}
                    <span css={css`
                      color: #ef4444; 
                      font-size: 11px; 
                      font-weight: 800;
                      font-family: monospace, monospace;
                      background: rgba(239, 68, 68, 0.05);
                      border: 1px solid rgba(239, 68, 68, 0.15);
                      padding: 2px 8px;
                      border-radius: 4px;
                      z-index: 2;
                      transition: all 0.3s ease;
                      &:hover {
                        background: rgba(239, 68, 68, 0.12);
                        box-shadow: 0 0 8px rgba(239, 68, 68, 0.4);
                        border-color: rgba(239, 68, 68, 0.4);
                      }
                    `}>
                      [ {displayScore.toFixed(0)}% ]
                    </span>
                  </div>

                  {/* Subtle red progress bar */}
                  <div css={css`height: 4px; background: var(--global-color-gray-200); border-radius: 2px; overflow: hidden; position: relative; z-index: 2;`}>
                    <div css={css`
                      height: 100%; width: ${displayScore}%;
                      background: #ef4444;
                      border-radius: 2px;
                      transition: width 1s ease;
                    `} />
                  </div>

                  <div css={css`display: flex; justify-content: space-between; align-items: center; font-size: 9px; color: var(--global-text-color-400); position: relative; z-index: 2;`}>
                    <span>{a.totalCount} evaluated traces · {a.projectCount} project{a.projectCount !== 1 ? 's' : ''}</span>
                    <span css={css`
                      color: #ef4444; 
                      font-weight: 800;
                      letter-spacing: 0.5px;
                      font-size: 9px;
                      font-family: monospace, monospace;
                    `}>CRITICAL</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Chat Bubble Cards ── */}
      <div css={css`
        display: flex; flex-direction: column;
        max-height: 360px; overflow-y: auto;
        padding: 6px 0;
        &::-webkit-scrollbar { width: 4px; }
        &::-webkit-scrollbar-thumb { background: var(--global-border-color-default); border-radius: 4px; }
      `}>
        {!hasAnyData ? (
          <div css={css`padding: 40px 24px; text-align: center; color: #9ca3af; font-size: 13px;`}>
            <div css={css`font-size: 36px; margin-bottom: 8px;`}>📊</div>
            No evaluation annotations found.
            <div css={css`font-size: 11px; margin-top: 4px; color: #d1d5db;`}>Run your evaluators to see results here.</div>
          </div>
        ) : (
          visibleAnnotations.map(ann => {
            const hasData = ann.avgScore !== null;
            const score = ann.avgScore ?? 0;
            const isGood = score >= 80;
            const isMid = score >= 60 && score < 80;
            const scoreColor = !hasData ? 'var(--global-text-color-500)' : isGood ? '#007AFF' : isMid ? '#C7820E' : '#ef4444';
            const bgColor = !hasData ? 'var(--global-color-gray-100)' : isGood ? 'rgba(0, 122, 255, 0.08)' : isMid ? 'rgba(199, 130, 14, 0.08)' : 'rgba(239, 68, 68, 0.08)';
            const isHov = hovered === ann.key;
            return (
              <div
                key={ann.key}
                onClick={() => { if (hasData) { setSelectedMetric(ann); setDrawerOpen(true); } }}
                css={css`
                  display: flex; align-items: flex-start; gap: 10px;
                  padding: 9px 14px;
                  opacity: ${hasData ? 1 : 0.38};
                  border-bottom: 1px solid var(--global-border-color-default);
                  background: ${isHov ? 'var(--global-color-gray-100)' : 'transparent'};
                  cursor: ${hasData ? 'pointer' : 'default'};
                  transition: background 0.15s ease;
                  &:last-child { border-bottom: none; }
                `}
                onMouseEnter={() => setHovered(ann.key)}
                onMouseLeave={() => setHovered(null)}
              >
                {/* Avatar */}
                <div css={css`
                  width: 32px; height: 32px; border-radius: 50%;
                  background: ${ann.color}18; border: 1.5px solid ${ann.color}40;
                  display: flex; align-items: center; justify-content: center;
                  font-size: 14px; flex-shrink: 0; margin-top: 2px;
                  transform: ${isHov ? 'scale(1.12)' : 'scale(1)'};
                  transition: transform 0.18s ease;
                `}>{ann.icon}</div>

                {/* Bubble */}
                <div css={css`
                  flex: 1;
                  background: ${bgColor};
                  border: 1px solid ${hasData ? ann.color + '28' : 'var(--global-border-color-default)'};
                  border-radius: 0 12px 12px 12px;
                  padding: 9px 12px; min-width: 0;
                  box-shadow: ${isHov ? `0 2px 10px ${ann.color}1a` : 'none'};
                  transition: box-shadow 0.18s ease;
                `}>
                  <div css={css`display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px;`}>
                    <div css={css`display: flex; align-items: center; gap: 6px;`}>
                      <span css={css`font-size: 12px; font-weight: 700; color: var(--global-text-color-900);`}>{ann.label}</span>
                      <span css={css`
                        font-size: 9px; font-weight: 700;
                        color: ${ann.kind === 'LLM' ? '#3b82f6' : '#10b981'};
                        background: ${ann.kind === 'LLM' ? 'rgba(59, 130, 246, 0.12)' : 'rgba(16, 185, 129, 0.12)'};
                        border: 1px solid ${ann.kind === 'LLM' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(16, 185, 129, 0.3)'};
                        padding: 1px 5px; border-radius: 10px; text-transform: uppercase; letter-spacing: 0.5px;
                      `}>{ann.kind}</span>
                    </div>
                    {hasData
                      ? <span css={css`font-size: 18px; font-weight: 800; color: ${scoreColor};`}>{score.toFixed(1)}%</span>
                      : <span css={css`font-size: 11px; color: var(--global-text-color-500); font-style: italic;`}>No data</span>
                    }
                  </div>
                  {hasData && (
                    <div css={css`height: 5px; background: var(--global-color-gray-200); border-radius: 4px; overflow: hidden; margin-bottom: 5px;`}>
                      <div css={css`
                        height: 100%; width: ${Math.min(100, score)}%;
                        background: linear-gradient(90deg, ${scoreColor}bb, ${scoreColor});
                        border-radius: 4px;
                        transition: width 1s cubic-bezier(0.34, 1.56, 0.64, 1);
                      `} />
                    </div>
                  )}
                  <div css={css`display: flex; justify-content: space-between; align-items: center;`}>
                    <span css={css`font-size: 10px; color: var(--global-text-color-500);`}>
                      {hasData ? `${ann.totalCount.toLocaleString()} evaluated · ${ann.projectCount} project${ann.projectCount !== 1 ? 's' : ''}` : 'Not run yet'}
                    </span>
                    {hasData && (
                      <span css={css`font-size: 10px; font-weight: 700; color: ${scoreColor}; display: flex; align-items: center; gap: 4px;`}>
                        {isGood ? '✅ Pass' : isMid ? '⚠️ Borderline' : '❌ Fail'}
                        <span css={css`font-size: 8px; color: var(--global-text-color-400); transition: transform 0.2s; transform: ${expandedKey === ann.key ? 'rotate(180deg)' : 'rotate(0deg)'};`}>▼</span>
                      </span>
                    )}
                  </div>

                  {expandedKey === ann.key && hasData && (
                    <div css={css`
                      margin-top: 10px;
                      padding-top: 10px;
                      border-top: 1px dashed var(--global-border-color-default);
                      display: flex;
                      flex-direction: column;
                      gap: 6px;
                    `}>
                      <div css={css`font-size: 9px; font-weight: 700; color: var(--global-text-color-400); text-transform: uppercase; letter-spacing: 0.5px;`}>
                        🏢 Project Name
                      </div>
                      {projects
                        .map((p) => {
                          const summary = p[ann.key];
                          if (summary && (summary.count ?? 0) > 0) {
                            return {
                              projectName: p.name,
                              meanScore: summary.meanScore,
                              count: summary.count
                            };
                          }
                          return null;
                        })
                        .filter((x): x is { projectName: string; meanScore: number; count: number } => x !== null)
                        .map((pb) => (
                          <div key={pb.projectName} css={css`display: flex; justify-content: space-between; align-items: center; font-size: 11px;`}>
                            <span css={css`color: var(--global-text-color-800); font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 140px;`}>
                              • {pb.projectName}
                            </span>
                            <div css={css`display: flex; gap: 8px; align-items: center;`}>
                              <span css={css`color: var(--global-text-color-400); font-size: 9px;`}>{pb.count} runs</span>
                              <span css={css`
                                color: ${pb.meanScore >= 80 ? '#10b981' : pb.meanScore >= 60 ? '#f59e0b' : '#ef4444'};
                                font-weight: 700;
                              `}>
                                {pb.meanScore.toFixed(0)}%
                              </span>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      {hasAnyData && (
        <div css={css`
          padding: 9px 16px;
          background: linear-gradient(135deg, var(--global-color-gray-100) 0%, var(--global-color-gray-200) 100%);
          border-top: 1px solid var(--global-border-color-default);
          display: flex; justify-content: space-between; align-items: center;
        `}>
          <span css={css`font-size: 11px; color: var(--global-text-color-500);`}>
            📋 {totalEvaluatedTraces.toLocaleString()} evaluated traces
          </span>
          <div css={css`display: flex; gap: 10px; align-items: center;`}>
            <span css={css`font-size: 10px; color: var(--global-text-color-500);`}>🟢 {passCount} · 🟡 {borderlineCount} · 🔴 {failCount}</span>
            <span css={css`font-size: 12px; font-weight: 800; color: ${avgCorrectness >= 80 ? '#10b981' : avgCorrectness >= 60 ? '#f59e0b' : '#ef4444'};`}>
              Avg: {avgCorrectness.toFixed(1)}%
            </span>
          </div>
        </div>
      )}
      <EvaluationIssueDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        selectedMetric={selectedMetric}
        timeRange={timeRange}
      />
    </div>
  );
}

// 3D Bar shape for Recharts — custom SVG with front/top/side faces
const Custom3DBar = (props: any) => {
  const { x, y, width, height, fill, index } = props;
  if (!width || width <= 0 || !height || height <= 0) return null;
  const d = 5; // depth offset for 3D
  const gradId = `grad3d-${index}-${fill?.replace('#', '')}`;
  const topColor = "rgba(255,255,255,0.35)";
  const sideColor = "rgba(0,0,0,0.22)";
  return (
    <g>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={fill} stopOpacity={1} />
          <stop offset="100%" stopColor={fill} stopOpacity={0.82} />
        </linearGradient>
      </defs>
      {/* Front face */}
      <rect x={x} y={y + d} width={width} height={height - d} fill={`url(#${gradId})`} rx={3} ry={3}
        style={{ filter: 'drop-shadow(1px 2px 4px rgba(0,0,0,0.18))' }}
      />
      {/* Top face */}
      <polygon
        points={`${x},${y + d} ${x + d},${y} ${x + width + d},${y} ${x + width},${y + d}`}
        fill={fill}
        opacity={0.75}
        style={{ filter: `brightness(1.3)` }}
      />
      {/* Right end cap face */}
      <polygon
        points={`${x + width},${y + d} ${x + width + d},${y} ${x + width + d},${y + height - d} ${x + width},${y + height}`}
        fill={fill}
        opacity={0.55}
        style={{ filter: `brightness(0.72)` }}
      />
      {/* Top shine strip */}
      <rect x={x} y={y + d} width={width} height={3} fill={topColor} rx={3} />
    </g>
  );
};


function getTimeRangeLabel(start?: Date | null, end?: Date | null): string {
  if (!start || !end) return "selected time range";
  const diffMs = Math.abs(end.getTime() - start.getTime());
  const diffMinutes = Math.round(diffMs / (1000 * 60));
  const diffHours = Math.round(diffMs / (1000 * 60 * 60));
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes <= 20) return "last 15 min";
  if (diffMinutes <= 75) return "last hour";
  if (diffHours <= 14) return `last ${diffHours} hours`;
  if (diffDays === 1 || (diffHours >= 20 && diffHours <= 26)) return "last day";
  if (diffDays >= 6 && diffDays <= 8) return "last 7 days";
  if (diffDays >= 27 && diffDays <= 32) return "last month";
  if (diffDays > 1) return `last ${diffDays} days`;

  return "selected calendar range";
}

function GlobalDashboardPageContent() {
  const { timeRange: contextTimeRange } = useTimeRange();

  const timeRange = useMemo(() => {
    const start = contextTimeRange.start;
    const end = contextTimeRange.end;
    if (start && end) return { start, end };
    if (!start && end) return { start: new Date(end.getTime() - ONE_MONTH_MS), end };
    if (start && !end) return { start, end: new Date(start.getTime() + ONE_MONTH_MS) };
    const now = new Date();
    return { start: new Date(now.getTime() - ONE_MONTH_MS), end: now };
  }, [contextTimeRange]);

  return (
    <main css={pageCSS}>
      <ErrorBoundary
        fallback={
          <Flex direction="column" alignItems="center" justifyContent="center" height="100%" gap="size-150">
            <Heading level={3}>Dashboard Telemetry Error</Heading>
            <Text color="text-500">Unable to load dashboard metrics. Please check network connection or retry.</Text>
            <button
              onClick={() => window.location.reload()}
              css={css`
                background: #00685f;
                color: #ffffff;
                border: none;
                border-radius: 8px;
                padding: 8px 16px;
                font-weight: 800;
                cursor: pointer;
              `}
            >
              Refresh Dashboard
            </button>
          </Flex>
        }
      >
        <Suspense fallback={<Loading />}>
          <GlobalDashboardContent timeRange={timeRange} />
        </Suspense>
      </ErrorBoundary>
    </main>
  );
}

function GlobalDashboardContent({
  timeRange,
}: {
  timeRange: { start: Date; end: Date };
}) {
  const data = useLazyLoadQuery<GlobalDashboardPageQuery>(
    graphql`
      query GlobalDashboardPageQuery($timeRange: TimeRange!) {
        projects(first: 100) {
          edges {
            node {
              id
              name
              gradientStartColor
              gradientEndColor
              traceCount(timeRange: $timeRange)
              costSummary(timeRange: $timeRange) {
                total {
                  cost
                  tokens
                }
              }
              topModelsByCost(timeRange: $timeRange) {
                id
                name
                costSummary(timeRange: $timeRange) {
                  total {
                    cost
                    tokens
                  }
                }
              }
              latencyMsP50: latencyMsQuantile(probability: 0.5, timeRange: $timeRange)
              traceAnnotationsNames
              traceAnnotationSummaries(timeRange: $timeRange) {
                name
                meanScore
                count
              }
              spanAnnotationAgentSummaries(timeRange: $timeRange) {
                name
                meanScore
                numScores
              }
              traceAnnotationSummary: spanAnnotationSummary(annotationName: "General_Correctness", timeRange: $timeRange) {
                meanScore
                count
              }
              piiAnnotation: spanAnnotationSummary(annotationName: "pii_detection", timeRange: $timeRange) {
                count
                meanScore
              }
              ann_instruction: spanAnnotationSummary(annotationName: "Instruction_Compliance", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_prompt: spanAnnotationSummary(annotationName: "Prompt_Adherence", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_token: spanAnnotationSummary(annotationName: "Token_Breakdown", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_trace: spanAnnotationSummary(annotationName: "Trace_Completeness", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_failure: spanAnnotationSummary(annotationName: "Failure_Rates", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_latency: spanAnnotationSummary(annotationName: "Span_Latency", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_role: spanAnnotationSummary(annotationName: "Role_Consistency", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_system: spanAnnotationSummary(annotationName: "System_Prompt_Leakage", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_fewshot: spanAnnotationSummary(annotationName: "Few_Shot_Effectiveness", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_translation: spanAnnotationSummary(annotationName: "Translation_Accuracy", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_crosslang: spanAnnotationSummary(annotationName: "CrossLanguage_Consistency", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_cultural: spanAnnotationSummary(annotationName: "Cultural_Appropriateness", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_format: spanAnnotationSummary(annotationName: "Format_Compliance", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_schema: spanAnnotationSummary(annotationName: "Schema_Validation", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_reqfields: spanAnnotationSummary(annotationName: "Required_Fields_Present", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_structure: spanAnnotationSummary(annotationName: "Structure_Correctness", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_regex: spanAnnotationSummary(annotationName: "Regex_Match", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_syntax: spanAnnotationSummary(annotationName: "Syntax_Correctness", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_compile: spanAnnotationSummary(annotationName: "Compilation_Success", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_efficiency: spanAnnotationSummary(annotationName: "Efficiency_of_Code", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_testcase: spanAnnotationSummary(annotationName: "Test_Case_Pass_Rate", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_security: spanAnnotationSummary(annotationName: "Security_Vulnerabilities", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_helpfulness: spanAnnotationSummary(annotationName: "Helpfulness", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_satisfaction: spanAnnotationSummary(annotationName: "Satisfaction_Score", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_responsetime: spanAnnotationSummary(annotationName: "Response_Time_Perception", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_convquality: spanAnnotationSummary(annotationName: "Conversational_Quality", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_domain: spanAnnotationSummary(annotationName: "Domain_Accuracy", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_uptodate: spanAnnotationSummary(annotationName: "Up_To_Date_Knowledge", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_factcheck: spanAnnotationSummary(annotationName: "Fact_Checking_Accuracy", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_citation: spanAnnotationSummary(annotationName: "Citation_Correctness", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_moderation: spanAnnotationSummary(annotationName: "Moderation", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_closed_qa: spanAnnotationSummary(annotationName: "Model-Graded Closed QA", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_retrieval: spanAnnotationSummary(annotationName: "Retrieval_vs_Generation", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_consistency: spanAnnotationSummary(annotationName: "Same_Input_Same_Output", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_stability: spanAnnotationSummary(annotationName: "Stability_Across_Runs", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_tempsens: spanAnnotationSummary(annotationName: "Temperature_Sensitivity", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_determinism: spanAnnotationSummary(annotationName: "Determinism", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_sql: spanAnnotationSummary(annotationName: "Sql_Evaluation", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_toolusage: spanAnnotationSummary(annotationName: "Tool_Usage_Correctness", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_toolinvoke: spanAnnotationSummary(annotationName: "Tool_Invocation", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_reasoning: spanAnnotationSummary(annotationName: "Multi_Step_Reasoning", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_contains: spanAnnotationSummary(annotationName: "Contains_Substring", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_refusal: spanAnnotationSummary(annotationName: "Is-Refusal", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_perplexity: spanAnnotationSummary(annotationName: "Perplexity", timeRange: $timeRange) {
                meanScore
                count
              }
            }
          }
        }
        evaluators(first: 200) {
          edges {
            node {
              id
              name
              kind
              isBuiltin
            }
          }
        }
      }
    `,
    {
      timeRange: {
        start: timeRange.start.toISOString(),
        end: timeRange.end.toISOString(),
      },
    },
    {
      fetchPolicy: "network-only",
    }
  );

  const projects = useMemo(() => {
    return data.projects.edges.map((edge) => {
      const node = edge.node;
      const traceSummaries = node.traceAnnotationSummaries;
      if (!traceSummaries || traceSummaries.length === 0) {
        return node;
      }
      const traceByName = new Map(
        traceSummaries.map((summary) => [
          normalizeAnnotationName(summary.name),
          summary,
        ])
      );
      const overrides: Record<
        string,
        { name: string; meanScore: number | null; count: number }
      > = {};
      for (const [key, meta] of Object.entries(ANN_META)) {
        const traceSummary = traceByName.get(normalizeAnnotationName(meta.label));
        if (traceSummary) {
          overrides[key] = traceSummary;
        }
      }
      return Object.keys(overrides).length > 0
        ? { ...node, ...overrides }
        : node;
    });
  }, [data]);

  // Compute Aggregations (Observability)
  const totalProjects = projects.length;
  const totalTraces = useMemo(() => {
    return projects.reduce((acc, p) => acc + p.traceCount, 0);
  }, [projects]);
  const totalCost = useMemo(() => {
    return projects.reduce((acc, p) => acc + (p.costSummary?.total?.cost ?? 0), 0);
  }, [projects]);
  const totalTokens = useMemo(() => {
    return projects.reduce((acc, p) => acc + (p.costSummary?.total?.tokens ?? 0), 0);
  }, [projects]);
  const avgLatency = useMemo(() => {
    const validLatencies = projects
      .map((p) => p.latencyMsP50)
      .filter((l): l is number => l != null && l > 0);
    if (validLatencies.length === 0) return 0;
    return validLatencies.reduce((acc, l) => acc + l, 0) / validLatencies.length;
  }, [projects]);

  // Compute Aggregations (Evaluation)
  const avgCorrectness = useMemo(() => {
    const scores = projects
      .map((p) => p.traceAnnotationSummary?.meanScore)
      .filter((s): s is number => s != null);
    if (scores.length === 0) return 0;
    return scores.reduce((acc, s) => acc + s, 0) / scores.length;
  }, [projects]);

  const totalEvaluatedTraces = useMemo(() => {
    return projects.reduce((acc, p) => acc + (p.traceAnnotationSummary?.count ?? 0), 0);
  }, [projects]);

  // Generate Sparkline Mock Curves based on aggregate metric values
  const projectsSparkData = useMemo(() => [
    { value: Math.max(1, totalProjects - 2) },
    { value: Math.max(1, totalProjects - 2) },
    { value: Math.max(1, totalProjects - 1) },
    { value: Math.max(1, totalProjects - 1) },
    { value: totalProjects },
    { value: totalProjects },
  ], [totalProjects]);

  const tracesSparkData = useMemo(() => [
    { value: totalTraces === 0 ? 0.05 : totalTraces * 0.2 },
    { value: totalTraces === 0 ? 0.15 : totalTraces * 0.6 },
    { value: totalTraces === 0 ? 0.08 : totalTraces * 0.35 },
    { value: totalTraces === 0 ? 0.22 : totalTraces * 0.9 },
    { value: totalTraces === 0 ? 0.18 : totalTraces * 0.7 },
    { value: totalTraces === 0 ? 0.35 : totalTraces * 1.3 },
    { value: totalTraces === 0 ? 0.25 : totalTraces },
  ], [totalTraces]);

  const costSparkData = useMemo(() => [
    { value: totalCost === 0 ? 0.02 : totalCost * 0.4 },
    { value: totalCost === 0 ? 0.04 : totalCost * 0.7 },
    { value: totalCost === 0 ? 0.01 : totalCost * 0.2 },
    { value: totalCost === 0 ? 0.06 : totalCost * 1.1 },
    { value: totalCost === 0 ? 0.03 : totalCost * 0.6 },
    { value: totalCost === 0 ? 0.05 : totalCost },
  ], [totalCost]);

  const latencySparkData = useMemo(() => [
    { value: avgLatency === 0 ? 0.1 : avgLatency * 0.9 },
    { value: avgLatency === 0 ? 0.25 : avgLatency * 1.25 },
    { value: avgLatency === 0 ? 0.08 : avgLatency * 0.8 },
    { value: avgLatency === 0 ? 0.32 : avgLatency * 1.15 },
    { value: avgLatency === 0 ? 0.15 : avgLatency * 0.95 },
    { value: avgLatency === 0 ? 0.22 : avgLatency },
  ], [avgLatency]);

  const correctnessSparkData = useMemo(() => [
    { value: avgCorrectness === 0 ? 0.05 : avgCorrectness * 0.95 },
    { value: avgCorrectness === 0 ? 0.25 : avgCorrectness * 1.05 },
    { value: avgCorrectness === 0 ? 0.08 : avgCorrectness * 0.9 },
    { value: avgCorrectness === 0 ? 0.32 : avgCorrectness * 1.02 },
    { value: avgCorrectness === 0 ? 0.22 : avgCorrectness },
  ], [avgCorrectness]);

  const evaluatedSparkData = useMemo(() => [
    { value: totalEvaluatedTraces === 0 ? 0.05 : totalEvaluatedTraces * 0.2 },
    { value: totalEvaluatedTraces === 0 ? 0.15 : totalEvaluatedTraces * 0.7 },
    { value: totalEvaluatedTraces === 0 ? 0.08 : totalEvaluatedTraces * 0.4 },
    { value: totalEvaluatedTraces === 0 ? 0.28 : totalEvaluatedTraces * 1.15 },
    { value: totalEvaluatedTraces === 0 ? 0.18 : totalEvaluatedTraces * 0.9 },
    { value: totalEvaluatedTraces === 0 ? 0.25 : totalEvaluatedTraces },
  ], [totalEvaluatedTraces]);

  const [metric, setMetric] = useState<"cost" | "traces" | "latency" | "tokens">("traces");
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [evalHoveredIndex, setEvalHoveredIndex] = useState<number | null>(null);

  // Checkbox project selection state
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());
  const [selectedModelName, setSelectedModelName] = useState<string>("All Models");

  // Collect all unique model names across projects in the selected time range
  const allModels = useMemo(() => {
    const models = new Set<string>();
    projects.forEach((p) => {
      p.topModelsByCost?.forEach((m) => {
        if (m.name) models.add(m.name);
      });
    });
    return ["All Models", ...Array.from(models).sort()];
  }, [projects]);

  const filteredProjectsCount = useMemo(() => {
    if (selectedModelName === "All Models") return totalProjects;
    return projects.filter(p => p.topModelsByCost?.some(m => m.name === selectedModelName)).length;
  }, [projects, selectedModelName, totalProjects]);

  const filteredTraces = useMemo(() => {
    if (selectedModelName === "All Models") return totalTraces;
    return projects
      .filter(p => p.topModelsByCost?.some(m => m.name === selectedModelName))
      .reduce((acc, p) => acc + p.traceCount, 0);
  }, [projects, selectedModelName, totalTraces]);

  const filteredCost = useMemo(() => {
    if (selectedModelName === "All Models") return totalCost;
    return projects.reduce((acc, p) => {
      const m = p.topModelsByCost?.find(model => model.name === selectedModelName);
      return acc + (m?.costSummary?.total?.cost ?? 0);
    }, 0);
  }, [projects, selectedModelName, totalCost]);

  const filteredTokens = useMemo(() => {
    if (selectedModelName === "All Models") return totalTokens;
    return projects.reduce((acc, p) => {
      const m = p.topModelsByCost?.find(model => model.name === selectedModelName);
      return acc + (m?.costSummary?.total?.tokens ?? 0);
    }, 0);
  }, [projects, selectedModelName, totalTokens]);

  const filteredLatency = useMemo(() => {
    if (selectedModelName === "All Models") return avgLatency;
    const validLatencies = projects
      .filter(p => p.topModelsByCost?.some(m => m.name === selectedModelName))
      .map((p) => p.latencyMsP50)
      .filter((l): l is number => l != null && l > 0);
    if (validLatencies.length === 0) return 0;
    return validLatencies.reduce((acc, l) => acc + l, 0) / validLatencies.length;
  }, [projects, selectedModelName, avgLatency]);
  const toggleProject = (id: string) => {
    setSelectedProjectIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // Empty set = all selected (show all)
  const activeProjects = selectedProjectIds.size === 0 ? projects : projects.filter(p => selectedProjectIds.has(p.id));

  // Recharts Data for Cost, Trace, or Latency Distribution — filtered by selection
  const chartData = useMemo(() => {
    return activeProjects
      .map((p, idx) => {
        let value = 0;
        if (selectedModelName === "All Models") {
          value = metric === "cost"
            ? (p.costSummary?.total?.cost ?? 0)
            : metric === "latency"
              ? (p.latencyMsP50 ?? 0)
              : metric === "tokens"
                ? (p.costSummary?.total?.tokens ?? 0)
                : p.traceCount;
        } else {
          const m = p.topModelsByCost?.find(model => model.name === selectedModelName);
          if (m) {
            value = metric === "cost"
              ? (m.costSummary?.total?.cost ?? 0)
              : metric === "latency"
                ? (p.latencyMsP50 ?? 0)
                : metric === "tokens"
                  ? (m.costSummary?.total?.tokens ?? 0)
                  : p.traceCount;
          }
        }
        return {
          name: p.name.length > 14 ? p.name.slice(0, 14) + '…' : p.name,
          value,
          color: chartColors[projects.indexOf(p) % chartColors.length]
        };
      })
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [activeProjects, metric, projects, selectedModelName]);

  const evalChartData = useMemo(() => {
    if (totalEvaluatedTraces === 0) return [];
    const correctCount = Math.round(totalEvaluatedTraces * (avgCorrectness / 100));
    const incorrectCount = totalEvaluatedTraces - correctCount;
    return [
      { name: "✅ Correct", value: correctCount, color: "#10b981" },
      { name: "❌ Incorrect", value: incorrectCount, color: "#ec4899" },
    ].filter((d) => d.value > 0);
  }, [totalEvaluatedTraces, avgCorrectness]);

  const fmtLatency = (ms: number) => {
    if (ms === 0) return "--";
    if (ms < 1) return `${ms.toFixed(2)}ms`;
    return ms < 1000 ? `${ms.toFixed(0)}ms` : `${(ms / 1000).toFixed(2)}s`;
  };

  // Custom tooltip for pie charts
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div css={{
          background: 'var(--global-color-gray-75)',
          border: '1px solid var(--global-border-color-default)',
          borderRadius: '12px',
          padding: '12px 16px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        }}>
          <p css={{ color: 'var(--global-text-color-500)', fontSize: '12px', margin: 0 }}>
            {payload[0].name}
          </p>
          <p css={{ color: 'var(--global-text-color-900)', fontSize: '18px', fontWeight: 700, margin: '4px 0 0 0' }}>
            {metric === "cost" ? costFormatter(payload[0].value) : payload[0].value.toLocaleString()}
          </p>
          <p css={{ color: 'var(--global-text-color-300)', fontSize: '11px', margin: '2px 0 0 0' }}>
            {((payload[0].value / chartData.reduce((acc, d) => acc + d.value, 0)) * 100).toFixed(1)}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div css={css`display: flex; flex-direction: column; gap: 24px; width: 100%; position: relative; z-index: 1;`}>
      {/* Top Header Bar with Model Filter before Time Range Selector */}
      <div css={headerCSS}>
        <div>
          <div css={titleCSS}>Dashboard</div>
          <div css={subtitleCSS}>Real-time observability & evaluation insights</div>
        </div>
        <div css={css`display: flex; align-items: center; gap: 12px;`}>
          {/* Rounded Modern Model Selector Dropdown */}
          <div css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            background: #ffffff;
            border: 1px solid var(--global-border-color-default, #e1e3e4);
            padding: 6px 14px;
            border-radius: 20px;
            transition: all 0.3s ease;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
            &:hover {
              border-color: var(--global-color-gray-300);
              box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
            }
          `}>
            <span css={css`
              font-size: 11px;
              font-weight: 800;
              color: #0f172a;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            `}>🤖 Model Filter:</span>
            <select
              value={selectedModelName}
              onChange={(e) => setSelectedModelName(e.target.value)}
              css={css`
                border: none;
                background: transparent;
                color: #0f172a;
                font-size: 12px;
                font-weight: 800;
                cursor: pointer;
                outline: none;
                padding-right: 4px;
                & option {
                  background: #ffffff;
                  color: #0f172a;
                }
              `}
            >
              {allModels.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <ConnectedTimeRangeSelector size="M" />
        </div>
      </div>

      {/* Global Observability Summary Grid */}
      <div css={globalStatGridCSS}>
        {/* Card 1: Total Projects */}
        <div css={globalStatCardCSS}>
          <div css={globalIconContainerCSS("#eff6ff", "#2563eb")}>
            <FolderOutline />
          </div>
          <div css={globalStatLabelCSS}>Total Projects</div>
          <div css={globalStatValueCSS}>{filteredProjectsCount.toLocaleString()}</div>
          <div css={globalStatHintCSS}>Active workspaces</div>
        </div>

        {/* Card 2: Total Traces */}
        <div css={globalStatCardCSS}>
          <div css={globalIconContainerCSS("#ecfdf5", "#059669")}>
            <ListOutline />
          </div>
          <div css={globalStatLabelCSS}>Total Traces</div>
          <div css={globalStatValueCSS}>{filteredTraces.toLocaleString()}</div>
          <div css={globalStatHintCSS}>Run count</div>
        </div>

        {/* Card 3: Total Cost */}
        <div css={globalStatCardCSS}>
          <div css={globalIconContainerCSS("#f5f3ff", "#7c3aed")}>
            <DollarSignOutline />
          </div>
          <div css={globalStatLabelCSS}>Total Cost</div>
          <div css={globalStatValueCSS}>{costFormatter(filteredCost)}</div>
          <div css={globalStatHintCSS}>Estimated USD</div>
        </div>

        {/* Card 4: Total Tokens */}
        <div css={globalStatCardCSS}>
          <div css={globalIconContainerCSS("#ecfeff", "#0891b2")}>
            <CubeOutline />
          </div>
          <div css={globalStatLabelCSS}>Total Tokens</div>
          <div css={globalStatValueCSS}>{intShortFormatter(filteredTokens)}</div>
          <div css={globalStatHintCSS}>Input + Output</div>
        </div>

        {/* Card 5: Avg Latency */}
        <div css={globalStatCardCSS}>
          <div css={globalIconContainerCSS("#fffbeb", "#d97706")}>
            <ClockOutline />
          </div>
          <div css={globalStatLabelCSS}>Avg Latency</div>
          <div css={globalStatValueCSS}>{fmtLatency(filteredLatency)}</div>
          <div css={globalStatHintCSS}>Median response time</div>
        </div>
      </div>

      <div css={splitGridCSS}>
        {/* Project Performance Comparison */}
        <ProjectPerformanceChartCard
          metric={metric}
          setMetric={setMetric}
          chartData={chartData}
          chartCardCSS={chartCardCSS}
          costFormatter={costFormatter}
          fmtLatency={fmtLatency}
          Custom3DBar={Custom3DBar}
        />

        {/* RIGHT COLUMN: Health Overview */}
        <HealthOverviewCard
          projects={projects}
          totalTokens={filteredTokens}
          totalCost={filteredCost}
        />
      </div>

      {/* ROW 2: Active Projects Health Table */}
      <ActiveProjectsHealthCard
        projects={projects}
        fmtLatency={fmtLatency}
        costFormatter={costFormatter}
      />

      {/* ROW 3: Available Evaluation Metrics & Top Projects Needing Attention */}
      <div css={evalGridCSS}>
        <AvailableEvaluationsCard
          projects={projects}
          evaluators={data.evaluators.edges.map((edge) => edge.node)}
          timeRangeLabel={getTimeRangeLabel(timeRange.start, timeRange.end)}
        />
        <TopProjectsNeedingAttentionCard projects={projects} />
      </div>
    </div>
  );
}

// ─── HELPER: MAPS METRIC KEYS TO DB NAMES ──────────────────────────────────────
const KEY_TO_DB_NAME: Record<string, string> = {
  ann_instruction: "Instruction_Compliance",
  ann_prompt: "Prompt_Adherence",
  ann_role: "Role_Consistency",
  ann_system: "System_Prompt_Leakage",
  ann_fewshot: "Few_Shot_Effectiveness",
  traceAnnotationSummary: "General_Correctness",
  ann_translation: "Translation_Accuracy",
  ann_crosslang: "CrossLanguage_Consistency",
  ann_cultural: "Cultural_Appropriateness",
  ann_format: "Format_Compliance",
  ann_schema: "Schema_Validation",
  ann_reqfields: "Required_Fields_Present",
  ann_structure: "Structure_Correctness",
  ann_regex: "Regex_Match",
  ann_syntax: "Syntax_Correctness",
  ann_compile: "Compilation_Success",
  ann_efficiency: "Efficiency_of_Code",
  ann_testcase: "Test_Case_Pass_Rate",
  ann_security: "Security_Vulnerabilities",
  ann_helpfulness: "Helpfulness",
  ann_satisfaction: "Satisfaction_Score",
  ann_responsetime: "Response_Time_Perception",
  ann_convquality: "Conversational_Quality",
  ann_domain: "Domain_Accuracy",
  ann_uptodate: "Up_To_Date_Knowledge",
  ann_factcheck: "Fact_Checking_Accuracy",
  ann_citation: "Citation_Correctness",
  ann_moderation: "Moderation",
  ann_closed_qa: "Model-Graded Closed QA",
  ann_retrieval: "Retrieval_vs_Generation",
  ann_consistency: "Same_Input_Same_Output",
  ann_stability: "Stability_Across_Runs",
  ann_tempsens: "Temperature_Sensitivity",
  ann_determinism: "Determinism",
  ann_sql: "Sql_Evaluation",
  ann_toolusage: "Tool_Usage_Correctness",
  ann_toolinvoke: "Tool_Invocation",
  ann_reasoning: "Multi_Step_Reasoning",
  ann_contains: "Contains_Substring",
  ann_refusal: "Is-Refusal",
  ann_perplexity: "Perplexity",
  ann_token: "Token_Breakdown",
  ann_trace: "Trace_Completeness",
  ann_failure: "Failure_Rates",
  ann_latency: "Span_Latency"
};

// ─── GRAPHQL QUERY: ISSUE DRAWER DATA ──────────────────────────────────────────
const globalDashboardPageDrawerQuery = graphql`
  query GlobalDashboardPageDrawerQuery($evaluationName: String!, $timeRange: TimeRange!) {
    projects(first: 100) {
      edges {
        node {
          id
          name
          evaluationIssues(evaluationName: $evaluationName, timeRange: $timeRange) {
            issueName
            severity
            confidenceLevel
            affectedTracesCount
            affectedTracesPercentage
            affectedProjects
            mostAffectedSpan
            rootCause
            avgValue
            firstSeenTimestamp
            lastSeenTimestamp
            exampleTraceId
            expectedValue
            actualValue
            recommendation
            relatedIssueGroup
          }
          evaluationTraces(evaluationName: $evaluationName, timeRange: $timeRange) {
            traceId
            projectName
            status
            evaluationScore
            issueName
            affectedComponent
            spanName
            modelName
            metricValue
            timestamp
            projectId
            spanId
          }
        }
      }
    }
  }
`;

// ─── DRAWER CONTAINER ──────────────────────────────────────────────────────────
function EvaluationIssueDrawer({
  isOpen,
  onClose,
  selectedMetric,
  timeRange
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedMetric: any;
  timeRange: any;
}) {
  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          onClick={onClose}
          css={css`
            position: fixed;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0, 0, 0, 0.45);
            z-index: 999;
            opacity: 1;
            transition: opacity 0.3s ease;
          `}
        />
      )}

      {/* Drawer */}
      <div
        css={css`
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          width: 75vw;
          max-width: 900px;
          min-width: 600px;
          background: var(--global-background-color-default);
          border-left: 1px solid var(--global-border-color-default);
          box-shadow: -10px 0 30px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          display: flex;
          flex-direction: column;
          transform: ${isOpen ? 'translate3d(0, 0, 0)' : 'translate3d(100%, 0, 0)'};
          transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          color: var(--global-text-color-900);
          overflow: hidden;
        `}
      >
        {isOpen && selectedMetric && (
          <ErrorBoundary>
            <Suspense fallback={<DrawerSkeleton onClose={onClose} selectedMetric={selectedMetric} />}>
              <EvaluationIssueDrawerContent
                selectedMetric={selectedMetric}
                timeRange={timeRange}
                onClose={onClose}
              />
            </Suspense>
          </ErrorBoundary>
        )}
      </div>
    </>
  );
}

// ─── DRAWER SKELETON LOADER ────────────────────────────────────────────────────
function DrawerSkeleton({ onClose, selectedMetric }: { onClose: () => void; selectedMetric: any }) {
  return (
    <div css={css`display: flex; flex-direction: column; height: 100%;`}>
      {/* Header */}
      <div css={css`padding: 24px; border-bottom: 1px solid var(--global-border-color-default); display: flex; justify-content: space-between; align-items: center; background: linear-gradient(135deg, var(--global-color-gray-100) 0%, var(--global-color-gray-200) 100%);`}>
        <div css={css`display: flex; flex-direction: column; gap: 8px;`}>
          <div css={css`display: flex; align-items: center; gap: 8px;`}>
            <span css={css`font-size: 20px;`}>{selectedMetric.icon}</span>
            <h2 css={css`font-size: 18px; font-weight: 800; margin: 0;`}>{selectedMetric.label}</h2>
          </div>
          <div css={css`width: 250px; height: 12px; background: var(--global-color-gray-200); border-radius: 4px; animation: pulse 1.5s infinite ease-in-out;`}></div>
        </div>
        <button onClick={onClose} css={css`background: transparent; border: none; font-size: 20px; color: var(--global-text-color-500); cursor: pointer;`}>✕</button>
      </div>

      {/* Tab Skeletons */}
      <div css={css`display: flex; border-bottom: 1px solid var(--global-border-color-default); background: var(--global-color-gray-70); padding: 0 16px; gap: 16px;`}>
        {[1, 2, 3].map(i => (
          <div key={i} css={css`padding: 12px 16px; width: 80px; height: 16px; background: var(--global-color-gray-200); margin: 8px 0; border-radius: 4px;`}></div>
        ))}
      </div>

      {/* Body Skeleton */}
      <div css={css`flex: 1; padding: 24px; display: flex; flex-direction: column; gap: 24px; overflow-y: auto;`}>
        {/* Grid cards */}
        <div css={css`display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px;`}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} css={css`height: 60px; background: var(--global-color-gray-50); border: 1px solid var(--global-border-color-default); border-radius: 12px;`}></div>
          ))}
        </div>
        {/* Paragraphs */}
        <div css={css`display: flex; flex-direction: column; gap: 12px;`}>
          <div css={css`width: 100%; height: 80px; background: var(--global-color-gray-50); border: 1px solid var(--global-border-color-default); border-radius: 12px;`}></div>
        </div>
      </div>
    </div>
  );
}

// ─── DRAWER CONTENTS ───────────────────────────────────────────────────────────
function EvaluationIssueDrawerContent({
  selectedMetric,
  timeRange,
  onClose
}: {
  selectedMetric: any;
  timeRange: any;
  onClose: () => void;
}) {
  const notifySuccess = useNotifySuccess();
  const dbName = KEY_TO_DB_NAME[selectedMetric.key] || selectedMetric.key;
  const [fetchKey, setFetchKey] = useState(0);

  // Fetch dynamic issue metadata from server
  const data = useLazyLoadQuery<GlobalDashboardPageDrawerQuery>(
    globalDashboardPageDrawerQuery,
    {
      evaluationName: dbName,
      timeRange: { start: timeRange.start, end: timeRange.end }
    },
    {
      fetchPolicy: fetchKey > 0 ? "network-only" : "store-or-network",
      fetchKey
    }
  );

  const [activeTab, setActiveTab] = useState<'overview' | 'issues' | 'traces' | 'failed_traces'>('overview');
  const [statusFilter, setStatusFilter] = useState<'all' | 'passed' | 'failed'>('all');
  const [activeIssueFilter, setActiveIssueFilter] = useState<string | null>(null);

  // ─── Dynamic Aggregation Layer ───
  const rawIssues: any[] = [];
  const rawTraces: any[] = [];
  const projectsEdges = data?.projects?.edges || [];

  for (const edge of projectsEdges) {
    const proj = edge?.node;
    if (proj) {
      const projectIssues = proj.evaluationIssues || [];
      for (const issue of projectIssues) {
        rawIssues.push({
          ...issue,
          projectId: proj.id
        });
      }
      const projectTraces = proj.evaluationTraces || [];
      for (const trace of projectTraces) {
        rawTraces.push(trace);
      }
    }
  }

  // Merge issues by issueName
  const mergedIssuesMap: Record<string, any> = {};
  let totalFailedCount = 0;

  for (const issue of rawIssues) {
    const name = issue.issueName;
    if (!mergedIssuesMap[name]) {
      mergedIssuesMap[name] = {
        issueName: name,
        severity: issue.severity,
        confidenceLevel: issue.confidenceLevel,
        affectedTracesCount: 0,
        affectedProjects: new Set<string>(),
        mostAffectedSpan: issue.mostAffectedSpan,
        rootCause: issue.rootCause,
        avgValue: issue.avgValue,
        firstSeenTimestamp: issue.firstSeenTimestamp,
        lastSeenTimestamp: issue.lastSeenTimestamp,
        exampleTraceId: issue.exampleTraceId,
        expectedValue: issue.expectedValue,
        actualValue: issue.actualValue,
        recommendation: issue.recommendation,
        relatedIssueGroup: issue.relatedIssueGroup,
        projectId: issue.projectId
      };
    }

    const m = mergedIssuesMap[name];
    m.affectedTracesCount += issue.affectedTracesCount;
    totalFailedCount += issue.affectedTracesCount;
    if (issue.affectedProjects) {
      for (const p of issue.affectedProjects) {
        m.affectedProjects.add(p);
      }
    }

    const issueFirst = new Date(issue.firstSeenTimestamp);
    const issueLast = new Date(issue.lastSeenTimestamp);
    if (issueFirst < new Date(m.firstSeenTimestamp)) m.firstSeenTimestamp = issue.firstSeenTimestamp;
    if (issueLast > new Date(m.lastSeenTimestamp)) m.lastSeenTimestamp = issue.lastSeenTimestamp;
  }

  const mergedIssues = Object.values(mergedIssuesMap).map((issue: any) => {
    const pct = (issue.affectedTracesCount / (selectedMetric.totalCount || 1)) * 100;
    return {
      ...issue,
      affectedProjects: Array.from(issue.affectedProjects),
      affectedTracesPercentage: parseFloat(pct.toFixed(1))
    };
  }).sort((a: any, b: any) => b.affectedTracesCount - a.affectedTracesCount);

  // Overview metrics
  const successRate = selectedMetric.totalCount > 0
    ? ((selectedMetric.totalCount - totalFailedCount) / selectedMetric.totalCount) * 100
    : 100.0;

  const threshold = selectedMetric.kind === 'LLM' ? '>= 80%' : '<3 sec';

  // Find worst project
  const projectFailureMap: Record<string, number> = {};
  for (const trace of rawTraces) {
    if (trace.status === 'Failed') {
      projectFailureMap[trace.projectName] = (projectFailureMap[trace.projectName] || 0) + 1;
    }
  }
  const worstProject = Object.entries(projectFailureMap).sort((a, b) => b[1] - a[1])[0]?.[0] || 'None';
  const commonFailure = mergedIssues[0]?.issueName || 'None';

  const lastEvaluatedDate = rawTraces.length > 0
    ? new Date(Math.max(...rawTraces.map(t => new Date(t.timestamp).getTime())))
    : new Date();
  const lastEvaluatedStr = lastEvaluatedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ', ' + lastEvaluatedDate.toLocaleDateString();

  // Dynamic AI Summary Text
  const aiSummaryText = mergedIssues.length > 0
    ? `${mergedIssues[0]?.issueName || 'Unknown Issue'} accounts for ${mergedIssues[0]?.affectedTracesPercentage ?? 0}% of failed traces. Optimizing the ${mergedIssues[0]?.mostAffectedSpan || 'target'} component in project ${worstProject || 'None'} is expected to improve the ${selectedMetric?.label || 'evaluation'} score significantly.`
    : "No significant failure patterns were detected. The evaluation completed successfully. All traces are within normal thresholds.";

  // Recommendations mapping
  const recommendations = mergedIssues.map((issue: any) => {
    return {
      title: `Optimize ${issue.mostAffectedSpan || 'component'}`,
      priority: issue.severity === 'Critical' ? 'High' : issue.severity === 'High' ? 'High' : 'Medium',
      estimatedImprovement: `${Math.round((issue.affectedTracesPercentage ?? 0) / 1.5)}%`,
      expectedImpact: `Resolves ${issue.affectedTracesCount ?? 0} failed traces related to ${issue.issueName || 'issue'}`,
      relatedIssue: issue.issueName
    };
  });

  // Filter traces dynamically
  const filteredTraces = useMemo(() => {
    return rawTraces.filter((trace: any) => {
      if (!activeIssueFilter) return true;
      return trace.issueName?.toLowerCase() === activeIssueFilter.toLowerCase();
    }).sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [rawTraces, activeIssueFilter]);

  const failedTraces = useMemo(() => {
    return filteredTraces.filter((trace: any) => trace.status?.toLowerCase() === 'failed');
  }, [filteredTraces]);

  const displayedTraces = useMemo(() => {
    let traces = filteredTraces;
    if (activeTab === 'failed_traces') {
      return traces.filter((trace: any) => trace.status?.toLowerCase() === 'failed');
    }
    if (statusFilter === 'passed') {
      return traces.filter((trace: any) => trace.status?.toLowerCase() === 'passed');
    }
    if (statusFilter === 'failed') {
      return traces.filter((trace: any) => trace.status?.toLowerCase() === 'failed');
    }
    return traces;
  }, [filteredTraces, activeTab, statusFilter]);

  // Formatting date helper
  const fmtTime = (isoString: string) => {
    const d = new Date(isoString);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleExportCSV = () => {
    const headers = [
      "Trace ID",
      "Project",
      "Status",
      "Score",
      "Issue Group",
      "Component",
      "Span Name",
      "Model/Tool",
      "Value",
      "Timestamp"
    ];

    const tracesToExport = displayedTraces;

    const rows = tracesToExport.map((trace: any) => [
      trace.traceId,
      trace.projectName,
      trace.status,
      trace.evaluationScore !== null ? `${trace.evaluationScore.toFixed(0)}%` : 'N/A',
      trace.issueName,
      trace.affectedComponent || 'N/A',
      trace.spanName,
      trace.modelName || 'N/A',
      trace.metricValue,
      fmtTime(trace.timestamp)
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(val => {
        const strVal = String(val ?? '');
        if (strVal.includes(',') || strVal.includes('"') || strVal.includes('\n') || strVal.includes('\r')) {
          return `"${strVal.replace(/"/g, '""')}"`;
        }
        return strVal;
      }).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const filename = `${selectedMetric.label.replace(/\\s+/g, '_')}_${activeTab === 'failed_traces' ? 'failed_' : ''}traces.csv`;

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div css={css`display: flex; flex-direction: column; height: 100%;`}>

      {/* ── HEADER ── */}
      <div css={css`
        padding: 20px 24px; 
        border-bottom: 1px solid var(--global-border-color-default); 
        display: flex; 
        justify-content: space-between; 
        align-items: center; 
        background: linear-gradient(135deg, var(--global-color-gray-100) 0%, var(--global-color-gray-200) 100%);
      `}>
        <div css={css`display: flex; flex-direction: column; gap: 4px;`}>
          {/* Breadcrumb */}
          <div css={css`display: flex; align-items: center; gap: 4px; font-size: 10px; font-weight: 700; color: var(--global-text-color-400); text-transform: uppercase; letter-spacing: 0.5px;`}>
            <span css={css`cursor: pointer; &:hover { color: var(--global-text-color-700); }`} onClick={onClose}>Evaluations</span>
            <span>&gt;</span>
            <span css={css`color: var(--global-text-color-700);`}>{selectedMetric.label}</span>
            {activeIssueFilter && (
              <>
                <span>&gt;</span>
                <span css={css`color: #ef4444;`}>{activeIssueFilter}</span>
              </>
            )}
          </div>
          <div css={css`display: flex; align-items: center; gap: 8px;`}>
            <span css={css`font-size: 20px;`}>{selectedMetric.icon}</span>
            <h2 css={css`font-size: 18px; font-weight: 800; margin: 0;`}>{selectedMetric.label}</h2>
            <span css={css`
              font-size: 9px; font-weight: 800; text-transform: uppercase;
              color: ${successRate >= 80 ? '#10b981' : '#ef4444'};
              background: ${successRate >= 80 ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)'};
              border: 1px solid ${successRate >= 80 ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'};
              padding: 2px 6px; border-radius: 8px;
            `}>
              {successRate >= 80 ? 'Pass' : 'Fail'}
            </span>
          </div>
          <div css={css`font-size: 11px; color: var(--global-text-color-400);`}>
            {selectedMetric.totalCount} traces · {selectedMetric.projectCount} Projects · Last Evaluated: {lastEvaluatedStr}
          </div>
        </div>
        <div css={css`display: flex; align-items: center; gap: 16px;`}>
          <div css={css`text-align: right;`}>
            <div css={css`font-size: 22px; font-weight: 800; color: ${successRate >= 80 ? '#007AFF' : '#ef4444'};`}>
              {(selectedMetric.avgScore ?? 0).toFixed(1)}%
            </div>
            <div css={css`font-size: 9px; font-weight: 700; color: var(--global-text-color-400); text-transform: uppercase;`}>Avg. Score</div>
          </div>
          <button
            onClick={onClose}
            css={css`
              background: transparent; border: none; font-size: 20px; color: var(--global-text-color-500); cursor: pointer;
              &:hover { color: var(--global-text-color-900); }
            `}
          >
            ✕
          </button>
        </div>
      </div>

      {/* ── TABS ── */}
      <div css={css`
        display: flex;
        border-bottom: 1px solid var(--global-border-color-default);
        background: var(--global-color-gray-70);
        padding: 0 16px;
      `}>
        {(['overview', 'issues', 'traces', 'failed_traces'] as const).map(tab => {
          const isActive = activeTab === tab;
          const count = tab === 'issues'
            ? mergedIssues.length
            : tab === 'traces'
              ? filteredTraces.length
              : tab === 'failed_traces'
                ? failedTraces.length
                : null;
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              css={css`
                padding: 12px 16px;
                font-size: 11px;
                font-weight: 800;
                border: none;
                background: transparent;
                color: ${isActive ? 'var(--global-text-color-900)' : 'var(--global-text-color-500)'};
                border-bottom: 2px solid ${isActive ? '#ef4444' : 'transparent'};
                cursor: pointer;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                transition: all 0.2s ease;
                &:hover { color: var(--global-text-color-900); }
              `}
            >
              {tab.replace('_', ' ')}
              {count !== null && <span css={css`
                margin-left: 6px; font-size: 9px; font-weight: 800;
                background: ${isActive ? '#ef4444' : 'var(--global-color-gray-200)'};
                color: ${isActive ? '#fff' : 'var(--global-text-color-500)'};
                padding: 2px 6px; border-radius: 10px;
              `}>{count}</span>}
            </button>
          );
        })}
      </div>

      {/* ── BODY ── */}
      <div css={css`flex: 1; overflow-y: auto; padding: 24px;`}>
        {((activeTab === 'overview' || activeTab === 'issues') && mergedIssues.length === 0) ? (
          /* Empty State */
          <div css={css`padding: 60px 24px; text-align: center; color: var(--global-text-color-400);`}>
            <div css={css`font-size: 40px; margin-bottom: 12px;`}>🎉</div>
            <div css={css`font-size: 14px; font-weight: 700; color: var(--global-text-color-900); margin-bottom: 6px;`}>No issues detected</div>
            <p css={css`font-size: 12px; margin: 0; max-width: 400px; margin: 0 auto; line-height: 1.5;`}>
              No significant failure patterns were detected. The evaluation completed successfully. All failed traces are isolated and do not form a common issue pattern.
            </p>
          </div>
        ) : (
          <>
            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && (
              <div css={css`display: flex; flex-direction: column; gap: 20px;`}>
                <div css={css`display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px;`}>
                  <div css={css`background: var(--global-color-gray-50); border: 1px solid var(--global-border-color-default); border-radius: 12px; padding: 14px; text-align: center;`}>
                    <span css={css`font-size: 9px; font-weight: 700; color: var(--global-text-color-400); text-transform: uppercase;`}>Success Rate</span>
                    <h3 css={css`font-size: 18px; font-weight: 800; color: ${successRate >= 80 ? '#10b981' : '#ef4444'}; margin: 6px 0 0 0;`}>{successRate.toFixed(1)}%</h3>
                  </div>
                  <div css={css`background: var(--global-color-gray-50); border: 1px solid var(--global-border-color-default); border-radius: 12px; padding: 14px; text-align: center;`}>
                    <span css={css`font-size: 9px; font-weight: 700; color: var(--global-text-color-400); text-transform: uppercase;`}>Evaluated Traces</span>
                    <h3 css={css`font-size: 18px; font-weight: 800; color: var(--global-text-color-900); margin: 6px 0 0 0;`}>{selectedMetric.totalCount}</h3>
                  </div>
                  <div css={css`background: var(--global-color-gray-50); border: 1px solid var(--global-border-color-default); border-radius: 12px; padding: 14px; text-align: center;`}>
                    <span css={css`font-size: 9px; font-weight: 700; color: var(--global-text-color-400); text-transform: uppercase;`}>Failed Traces</span>
                    <h3 css={css`font-size: 18px; font-weight: 800; color: #ef4444; margin: 6px 0 0 0;`}>{totalFailedCount}</h3>
                  </div>
                  <div css={css`background: var(--global-color-gray-50); border: 1px solid var(--global-border-color-default); border-radius: 12px; padding: 14px; text-align: center;`}>
                    <span css={css`font-size: 9px; font-weight: 700; color: var(--global-text-color-400); text-transform: uppercase;`}>Threshold</span>
                    <h3 css={css`font-size: 18px; font-weight: 800; color: #8b5cf6; margin: 6px 0 0 0;`}>{threshold}</h3>
                  </div>
                  <div css={css`background: var(--global-color-gray-50); border: 1px solid var(--global-border-color-default); border-radius: 12px; padding: 14px; text-align: center;`}>
                    <span css={css`font-size: 9px; font-weight: 700; color: var(--global-text-color-400); text-transform: uppercase;`}>Worst Project</span>
                    <h3 css={css`font-size: 13px; font-weight: 800; color: var(--global-text-color-900); margin: 8px 0 0 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`}>{worstProject}</h3>
                  </div>
                  <div css={css`background: var(--global-color-gray-50); border: 1px solid var(--global-border-color-default); border-radius: 12px; padding: 14px; text-align: center;`}>
                    <span css={css`font-size: 9px; font-weight: 700; color: var(--global-text-color-400); text-transform: uppercase;`}>Common Failure</span>
                    <h3 css={css`font-size: 12px; font-weight: 800; color: #ef4444; margin: 8px 0 0 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`} title={commonFailure}>{commonFailure}</h3>
                  </div>
                </div>

                <div css={css`border-top: 1px solid var(--global-border-color-default); padding-top: 18px;`}>
                  <h4 css={css`margin: 0 0 10px 0; font-size: 11px; font-weight: 800; color: var(--global-text-color-500); text-transform: uppercase; letter-spacing: 0.5px;`}>
                    ✨ AI-Generated Evaluation Summary
                  </h4>
                  <div css={css`
                    background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%);
                    border: 1px solid rgba(59, 130, 246, 0.15);
                    padding: 16px; border-radius: 12px; font-size: 12px; line-height: 1.5; color: var(--global-text-color-800);
                  `}>
                    {aiSummaryText}
                  </div>
                </div>
              </div>
            )}

            {/* ISSUES TAB */}
            {activeTab === 'issues' && (
              <div css={css`display: flex; flex-direction: column; gap: 16px;`}>
                <div css={css`font-size: 12px; font-weight: 800; color: var(--global-text-color-500); text-transform: uppercase; letter-spacing: 0.5px;`}>
                  Detected Issues ({mergedIssues.length})
                </div>
                <div css={css`display: flex; flex-direction: column; gap: 12px;`}>
                  {mergedIssues.map((issue: any, index: number) => (
                    <div key={index} css={css`
                      background: var(--global-color-gray-75);
                      border: 1px solid var(--global-border-color-default);
                      border-radius: 12px;
                      padding: 16px;
                      display: flex;
                      flex-direction: column;
                      gap: 12px;
                      transition: all 0.2s ease;
                      &:hover { border-color: rgba(239, 68, 68, 0.25); box-shadow: 0 4px 14px rgba(0,0,0,0.04); }
                    `}>
                      <div css={css`display: flex; justify-content: space-between; align-items: center;`}>
                        <div css={css`display: flex; align-items: center; gap: 8px;`}>
                          <span css={css`
                            width: 20px; height: 20px; border-radius: 50%; background: #ef4444; color: #fff;
                            display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 800;
                          `}>{index + 1}</span>
                          <span css={css`font-size: 13px; font-weight: 800; color: var(--global-text-color-900);`}>{issue.issueName}</span>
                          <span css={css`
                            font-size: 8px; font-weight: 800; text-transform: uppercase;
                            color: ${issue.severity === 'Critical' ? '#ef4444' : '#f59e0b'};
                            background: ${issue.severity === 'Critical' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(245, 158, 11, 0.08)'};
                            padding: 2px 6px; border-radius: 8px;
                          `}>{issue.severity}</span>
                          {issue.confidenceLevel && (
                            <span css={css`font-size: 10px; color: var(--global-text-color-400);`}>
                              ({Math.round(issue.confidenceLevel * 100)}% conf)
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            setActiveIssueFilter(issue.issueName);
                            setActiveTab('traces');
                          }}
                          css={css`
                            font-size: 11px; font-weight: 700; color: #3b82f6; background: transparent; border: none; cursor: pointer;
                            &:hover { text-decoration: underline; }
                          `}
                        >
                          View similar traces →
                        </button>
                      </div>

                      <div css={css`display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; font-size: 11px; border-top: 1px dashed var(--global-border-color-default); border-bottom: 1px dashed var(--global-border-color-default); padding: 10px 0;`}>
                        <div>
                          <div css={css`color: var(--global-text-color-400); font-weight: 700; text-transform: uppercase; font-size: 8.5px;`}>Affected Traces</div>
                          <div css={css`font-size: 13px; font-weight: 800; color: var(--global-text-color-900); margin-top: 4px;`}>
                            {issue.affectedTracesCount} <span css={css`font-size: 10px; font-weight: 500; color: var(--global-text-color-500);`}>({issue.affectedTracesPercentage}%)</span>
                          </div>
                        </div>
                        <div>
                          <div css={css`color: var(--global-text-color-400); font-weight: 700; text-transform: uppercase; font-size: 8.5px;`}>Affected Component</div>
                          <div css={css`font-size: 12px; font-weight: 800; color: var(--global-text-color-900); margin-top: 4px; font-family: monospace;`}>
                            {issue.mostAffectedSpan}
                          </div>
                        </div>
                        <div>
                          <div css={css`color: var(--global-text-color-400); font-weight: 700; text-transform: uppercase; font-size: 8.5px;`}>Avg. Metric Value</div>
                          <div css={css`font-size: 13px; font-weight: 800; color: #f59e0b; margin-top: 4px;`}>
                            {issue.avgValue}
                          </div>
                        </div>
                        <div>
                          <div css={css`color: var(--global-text-color-400); font-weight: 700; text-transform: uppercase; font-size: 8.5px;`}>Affected Projects</div>
                          <div css={css`display: flex; gap: 4px; flex-wrap: wrap; margin-top: 4px;`}>
                            {issue.affectedProjects.map((p: string) => (
                              <span key={p} css={css`font-size: 9px; font-weight: 700; background: var(--global-color-gray-200); color: var(--global-text-color-600); padding: 1px 6px; border-radius: 4px;`}>{p}</span>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div css={css`font-size: 11.5px; line-height: 1.5; color: var(--global-text-color-800);`}>
                        <strong>Root Cause: </strong> {issue.rootCause}
                      </div>

                      <div css={css`display: flex; justify-content: space-between; align-items: center; font-size: 10px; color: var(--global-text-color-400);`}>
                        <span>First seen: {fmtTime(issue.firstSeenTimestamp)} · Last seen: {fmtTime(issue.lastSeenTimestamp)}</span>
                        {issue.exampleTraceId && (
                          <Link
                            to={`/projects/${issue.projectId || projectsEdges[0]?.node?.id || "unknown"}/traces/${issue.exampleTraceId}?evaluation=${encodeURIComponent(selectedMetric.label)}&issue=${encodeURIComponent(issue.issueName)}&severity=${issue.severity}&rootCause=${encodeURIComponent(issue.rootCause)}&affectedSpan=${encodeURIComponent(issue.mostAffectedSpan)}&expected=${encodeURIComponent(issue.expectedValue)}&actual=${encodeURIComponent(issue.actualValue)}&recommendation=${encodeURIComponent(issue.recommendation)}&relatedIssueGroup=${issue.relatedIssueGroup}`}
                            css={css`color: #3b82f6; font-weight: 700; &:hover { text-decoration: underline; }`}
                          >
                            Inspect Example Trace ↗
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Dynamic Recommendations */}
                {recommendations.length > 0 && (
                  <div css={css`margin-top: 12px; border-top: 1px solid var(--global-border-color-default); padding-top: 20px;`}>
                    <h4 css={css`margin: 0 0 12px 0; font-size: 12px; font-weight: 800; color: var(--global-text-color-500); text-transform: uppercase; letter-spacing: 0.5px;`}>
                      🚀 AI-Recommended Optimizations
                    </h4>
                    <div css={css`display: flex; flex-direction: column; gap: 10px;`}>
                      {recommendations.map((rec: any, idx: number) => (
                        <div key={idx} css={css`
                          background: var(--global-color-gray-50);
                          border: 1px solid var(--global-border-color-default);
                          border-radius: 12px;
                          padding: 12px 16px;
                          display: flex;
                          justify-content: space-between;
                          align-items: center;
                          transition: all 0.2s ease;
                          &:hover { border-color: rgba(59, 130, 246, 0.3); transform: translateX(2px); }
                        `}>
                          <div>
                            <div css={css`font-size: 12px; font-weight: 700; color: var(--global-text-color-900);`}>{rec.title}</div>
                            <div css={css`font-size: 10px; color: var(--global-text-color-500); margin-top: 2px;`}>
                              {rec.expectedImpact} · Related to <span css={css`font-weight: 600; color: #ef4444;`}>{rec.relatedIssue}</span>
                            </div>
                          </div>
                          <div css={css`display: flex; gap: 12px; align-items: center;`}>
                            <span css={css`
                              font-size: 8px; font-weight: 800; color: ${rec.priority === 'High' ? '#ef4444' : '#f59e0b'};
                              background: ${rec.priority === 'High' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(245, 158, 11, 0.08)'};
                              padding: 2px 6px; border-radius: 8px; text-transform: uppercase;
                            `}>{rec.priority}</span>
                            <div css={css`text-align: right;`}>
                              <div css={css`font-size: 12px; font-weight: 800; color: #10b981;`}>+{rec.estimatedImprovement}</div>
                              <div css={css`font-size: 8px; color: var(--global-text-color-400); text-transform: uppercase;`}>Improve</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* TRACES TAB */}
            {(activeTab === 'traces' || activeTab === 'failed_traces') && (
              <div css={css`display: flex; flex-direction: column; gap: 12px;`}>

                {/* Status Filters (Only for Traces Tab) */}
                {activeTab === 'traces' && (
                  <div css={css`display: flex; gap: 8px; align-items: center; margin-bottom: 4px;`}>
                    <span css={css`font-size: 11px; font-weight: 700; color: var(--global-text-color-400); text-transform: uppercase; letter-spacing: 0.5px;`}>Filter Status:</span>
                    {(['all', 'passed', 'failed'] as const).map(status => {
                      const isActive = statusFilter === status;
                      return (
                        <button
                          key={status}
                          onClick={() => setStatusFilter(status)}
                          css={css`
                            font-size: 9px; font-weight: 800; text-transform: uppercase;
                            padding: 4px 10px; border-radius: 12px; border: 1px solid var(--global-border-color-default);
                            cursor: pointer;
                            background: ${isActive ? 'var(--global-color-gray-900)' : 'var(--global-color-gray-50)'};
                            color: ${isActive ? '#fff' : 'var(--global-text-color-600)'};
                            transition: all 0.15s ease;
                            &:hover {
                              background: ${isActive ? 'var(--global-color-gray-900)' : 'var(--global-color-gray-150)'};
                              color: ${isActive ? '#fff' : 'var(--global-text-color-900)'};
                            }
                          `}
                        >
                          {status}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Active Filter Chips */}
                {activeIssueFilter && (
                  <div css={css`display: flex; gap: 8px; align-items: center; margin-bottom: 8px;`}>
                    <span css={css`font-size: 11px; font-weight: 700; color: var(--global-text-color-400);`}>Active Filter:</span>
                    <span css={css`
                      font-size: 10px; font-weight: 600; color: #ef4444; background: rgba(239, 68, 68, 0.08);
                      border: 1px solid rgba(239, 68, 68, 0.2); padding: 2px 8px; border-radius: 12px;
                      display: flex; align-items: center; gap: 6px;
                    `}>
                      {activeIssueFilter}
                      <button
                        onClick={() => setActiveIssueFilter(null)}
                        css={css`background: transparent; border: none; color: #ef4444; cursor: pointer; font-weight: 800; font-size: 10px;`}
                      >
                        ✕
                      </button>
                    </span>
                    <span css={css`font-size: 10px; color: var(--global-text-color-400); font-weight: 600;`}>
                      ({displayedTraces.length} matching traces)
                    </span>
                  </div>
                )}

                {/* Traces Table */}
                <div css={css`overflow-x: auto; border: 1px solid var(--global-border-color-default); border-radius: 12px; background: var(--global-color-gray-75);`}>
                  <table css={css`width: 100%; border-collapse: collapse; text-align: left; font-size: 11px;`}>
                    <thead>
                      <tr css={css`background: var(--global-color-gray-100); border-bottom: 1px solid var(--global-border-color-default); color: var(--global-text-color-500); font-weight: 700;`}>
                        <th css={css`padding: 10px 14px;`}>Trace ID</th>
                        <th css={css`padding: 10px 14px;`}>Project</th>
                        <th css={css`padding: 10px 14px;`}>Status</th>
                        <th css={css`padding: 10px 14px;`}>Score</th>
                        <th css={css`padding: 10px 14px;`}>Issue Group</th>
                        <th css={css`padding: 10px 14px;`}>Component</th>
                        <th css={css`padding: 10px 14px;`}>Span Name</th>
                        <th css={css`padding: 10px 14px;`}>Model / Tool</th>
                        <th css={css`padding: 10px 14px;`}>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedTraces.map((trace: any) => {
                        const issueObj = mergedIssues.find(i => i.issueName === trace.issueName);
                        const params = new URLSearchParams({
                          evaluation: selectedMetric.label,
                          issue: trace.issueName,
                          severity: issueObj?.severity || "High",
                          rootCause: issueObj?.rootCause || "Evaluation check failed",
                          affectedComponent: trace.affectedComponent || "LLM",
                          affectedSpan: trace.spanName,
                          expected: issueObj?.expectedValue || "Pass",
                          actual: trace.metricValue,
                          recommendation: issueObj?.recommendation || "Review trace logs.",
                          relatedIssueGroup: issueObj?.relatedIssueGroup || "unknown_issue"
                        });
                        const traceUrl = `/projects/${trace.projectId}/traces/${trace.traceId}?${params.toString()}&selectedSpanNodeId=${encodeURIComponent(trace.spanId)}`;

                        return (
                          <tr key={`${trace.traceId}-${trace.spanName}-${trace.issueName}-${trace.status}`} css={css`
                            border-bottom: 1px solid var(--global-border-color-default);
                            color: var(--global-text-color-800);
                            transition: background 0.15s ease;
                            &:hover { background: var(--global-color-gray-100); }
                            &:last-child { border-bottom: none; }
                          `}>
                            <td css={css`padding: 10px 14px;`}>
                              <Link to={traceUrl} css={css`color: #3b82f6; font-weight: 700; &:hover { text-decoration: underline; }`}>
                                {trace.traceId.slice(0, 8)}...
                              </Link>
                            </td>
                            <td css={css`padding: 10px 14px; font-weight: 600;`}>{trace.projectName}</td>
                            <td css={css`padding: 10px 14px;`}>
                              <span css={css`
                                font-size: 8px; font-weight: 800; text-transform: uppercase;
                                color: ${trace.status === 'Failed' ? '#ef4444' : '#10b981'};
                                background: ${trace.status === 'Failed' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(16, 185, 129, 0.08)'};
                                padding: 2px 6px; border-radius: 6px;
                              `}>
                                {trace.status}
                              </span>
                            </td>
                            <td css={css`padding: 10px 14px; font-weight: 700;`}>
                              {trace.evaluationScore !== null ? `${trace.evaluationScore.toFixed(0)}%` : 'N/A'}
                            </td>
                            <td css={css`padding: 10px 14px; color: #ef4444; font-weight: 600;`}>{trace.issueName}</td>
                            <td css={css`padding: 10px 14px; font-family: monospace;`}>{trace.affectedComponent}</td>
                            <td css={css`padding: 10px 14px; font-family: monospace;`}>{trace.spanName}</td>
                            <td css={css`padding: 10px 14px; color: var(--global-text-color-500);`}>{trace.modelName || 'N/A'}</td>
                            <td css={css`padding: 10px 14px; color: var(--global-text-color-400);`}>{fmtTime(trace.timestamp)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── FOOTER ── */}
      <div css={css`
        padding: 12px 24px;
        background: linear-gradient(135deg, var(--global-color-gray-100) 0%, var(--global-color-gray-200) 100%);
        border-top: 1px solid var(--global-border-color-default);
        display: flex;
        justify-content: space-between;
        align-items: center;
      `}>
        <div css={css`display: flex; gap: 8px;`}>
          <button
            onClick={handleExportCSV}
            css={css`
              font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
              color: var(--global-text-color-600); background: var(--global-color-gray-50);
              border: 1px solid var(--global-border-color-default); padding: 5px 12px; border-radius: 6px; cursor: pointer;
              &:hover { background: var(--global-color-gray-100); color: var(--global-text-color-900); }
            `}
          >
            📥 Export Results
          </button>
        </div>
        <button
          onClick={() => {
            setFetchKey(prev => prev + 1);
            notifySuccess({
              title: "Refreshing Evaluations",
              message: "Querying latest annotation outputs...",
              expireMs: 1500
            });
          }}
          css={css`
            font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
            color: #10b981; background: rgba(16, 185, 129, 0.05);
            border: 1px solid rgba(16, 185, 129, 0.15); padding: 5px 12px; border-radius: 6px; cursor: pointer;
            &:hover { background: rgba(16, 185, 129, 0.1); }
          `}
        >
          🔄 Refresh Evaluation
        </button>
      </div>
    </div>
  );
}